export type CarType = 'Sedán' | 'SUV' | 'Pick-up' | 'Hatchback' | 'Deportivo'

export interface TrimLevel {
  name: string
  priceEstimate: string
  auctionEstimate: [number, number]
  features: string[]
}

export interface CarModel {
  id: string
  make: string
  model: string
  yearRange: string
  auctionRange: { min: number; max: number }
  priceRange: { min: number; max: number }
  type: CarType
  engine: string
  description: string
  trims: TrimLevel[]
}

// ═══════════════════════════════════════
// IAAI BUYER FEE SCHEDULE (2024-2025)
// Calibrated to real auction data
// ═══════════════════════════════════════
export function getIAAIBuyerFee(bid: number): number {
  if (bid < 100) return 40
  if (bid < 200) return 65
  if (bid < 300) return 100
  if (bid < 400) return 125
  if (bid < 500) return 125
  if (bid < 600) return 140
  if (bid < 700) return 140
  if (bid < 800) return 165
  if (bid < 900) return 205
  if (bid < 1000) return 245
  if (bid < 1200) return 285
  if (bid < 1300) return 330
  if (bid < 1500) return 370
  if (bid < 1600) return 410
  if (bid < 1800) return 450
  if (bid < 2000) return 450
  if (bid < 2400) return 535
  if (bid < 2500) return 575
  if (bid < 3000) return 615
  if (bid < 3500) return 655
  if (bid < 4000) return 695
  if (bid < 4500) return 740
  if (bid < 5000) return 780
  if (bid < 6000) return 860
  if (bid < 7500) return 945
  if (bid < 10000) return 1065
  if (bid < 15000) return 1230
  if (bid < 20000) return 1395
  return 1475
}

// ═══════════════════════════════════════
// TOTAL LANDED COST CALCULATION
// Auction Bid → Total cost in El Salvador
// Calibrated against real 2021 Sentra SR import:
//   Bid $3,325 → Final ~$9,900
// ═══════════════════════════════════════
export function calculateTotalCost(auctionBid: number): number {
  // ── Auction fees ──
  const iaaiBuyerFee = getIAAIBuyerFee(auctionBid)
  const internetFee = 110
  const serviceFee = 95
  const environmentalFee = 15
  const titleHandlingFee = 20
  const auctionFees =
    iaaiBuyerFee +
    internetFee +
    serviceFee +
    environmentalFee +
    titleHandlingFee

  // ── Transport ──
  const groundTow = 360
  const boatFreight = 1300
  const transport = groundTow + boatFreight // $1,660

  // ── CIF ──
  const cif = auctionBid + auctionFees + transport

  // ── Insurance (1% of CIF) ──
  const insurance = cif * 0.01
  const cifInsured = cif + insurance

  // ── Taxes (calculated on CIF + Insurance) ──
  const dai = cifInsured * 0.25 // 25% import duty
  const iva = cifInsured * 0.1625 // 16.25% IVA
  const aiv = cifInsured * 0.01 // 1% additional

  // ── Fixed government fees ──
  const pe = 40 // Permiso de Entrada
  const primeraMatricula = 306 // First registration + plates
  const inspeccion = 18 // Inspection
  const tramiteAduanero = 113 // Customs processing

  // ── Post-import costs ──
  const reparaciones = 1300 // parts + labor average
  const comision = 250 // commission
  const circulacion = 150 // circulation costs

  const total =
    cifInsured +
    dai +
    iva +
    aiv +
    pe +
    primeraMatricula +
    inspeccion +
    tramiteAduanero +
    reparaciones +
    comision +
    circulacion

  return Math.round(total / 100) * 100
}

// ═══════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════
function formatPriceRange(min: number, max: number): string {
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`
}

export function getCarImageUrl(
  make: string,
  model: string,
  year: number,
): string {
  const cleanModel = model
    .replace(/\s*hatchback/i, '')
    .replace(/^3$/, '3')
    .trim()
  return `https://cdn.imagin.studio/getimage?customer=hrjavascript-mastery&make=${encodeURIComponent(make)}&modelFamily=${encodeURIComponent(cleanModel)}&modelYear=${year}&angle=1`
}

export function getYearsFromRange(yearRange: string): number[] {
  const parts = yearRange.split('-').map(Number)
  if (parts.length === 1) return [parts[0]]
  const [start, end] = parts
  const years: number[] = []
  for (let y = start; y <= end; y++) years.push(y)
  return years
}

// ═══════════════════════════════════════
// AUCTION BID CALIBRATION
// Raw auction data = "typical listed/archived" values from IAAI/Copart.
// Real winning bids are lower than listed values.
//
// Calibrated against TWO real data points:
//   1) 2021 Sentra SR: actual bids $2,150 - $3,300
//      Raw SR (2020 base) = [3800, 5000], year-adjusted = [4009, 5850]
//      → 2150/4009 = 0.536 (min), 3300/5850 = 0.564 (max)
//   2) 2022 VW Taos SE: actual bids $3,000 - $4,600
//      Raw SE (adjusted) = [5500, 8100]
//      → 3000/5500 = 0.545 (min), 4600/8100 = 0.568 (max)
//
// Factors:
//   MIN = 0.54 → damaged vehicles (engine/frame/flood)
//   MAX = 0.57 → max profitable bid (light damage, worth importing)
// ═══════════════════════════════════════
const AUCTION_CALIBRATION_MIN = 0.54
const AUCTION_CALIBRATION_MAX = 0.57

function calibrateMinBid(estimatedBid: number): number {
  return Math.round(estimatedBid * AUCTION_CALIBRATION_MIN)
}

function calibrateMaxBid(estimatedBid: number): number {
  return Math.round(estimatedBid * AUCTION_CALIBRATION_MAX)
}

// ═══════════════════════════════════════
// INTER-YEAR DEPRECIATION RATES (PER MAKE)
// How much auction values increase per newer model year.
// Higher rate = newer years cost significantly more at auction.
//
// Driven by: insurance payout values, brand resale retention,
// parts costs, and enthusiast demand.
//
// minRate = heavily damaged vehicles (slower appreciation)
// maxRate = lightly damaged vehicles (faster appreciation)
//
// Calibrated against real data:
//   Nissan Sentra SR 2021: $2,150-$3,300 actual bids
//     → 5%/16% rates produce $2,155/$3,306 ✓
//
// ═══════════════════════════════════════
interface InterYearRates {
  minRate: number // rate for heavily damaged (min bid)
  maxRate: number // rate for lightly damaged (max bid)
}

const MAKE_INTER_YEAR_RATES: Record<string, InterYearRates> = {
  // ── Japanese (strong resale) ──
  Toyota: { minRate: 0.06, maxRate: 0.18 }, // Best resale in the market
  Honda: { minRate: 0.055, maxRate: 0.16 }, // Very strong, slightly below Toyota
  Mazda: { minRate: 0.05, maxRate: 0.15 }, // Premium positioning, solid resale
  Subaru: { minRate: 0.06, maxRate: 0.17 }, // Enthusiast demand (WRX, BRZ)
  Nissan: { minRate: 0.05, maxRate: 0.16 }, // Calibrated: Sentra SR 2021 ✓

  // ── Korean (improving resale) ──
  Hyundai: { minRate: 0.045, maxRate: 0.13 }, // Improving but historically lower
  Kia: { minRate: 0.045, maxRate: 0.13 }, // Same platform as Hyundai

  // ── German (luxury = fast depreciation) ──
  Volkswagen: { minRate: 0.05, maxRate: 0.14 }, // Moderate, better than luxury
  BMW: { minRate: 0.04, maxRate: 0.12 }, // Luxury, expensive repairs
  Audi: { minRate: 0.04, maxRate: 0.12 }, // Luxury, expensive repairs

  // ── American ──
  Ford: { minRate: 0.045, maxRate: 0.13 }, // Moderate depreciation
  Chevrolet: { minRate: 0.045, maxRate: 0.13 }, // Moderate depreciation
  Dodge: { minRate: 0.05, maxRate: 0.15 }, // Muscle car enthusiast demand
}

// Default fallback for any make not in the table
const DEFAULT_INTER_YEAR_RATES: InterYearRates = {
  minRate: 0.05,
  maxRate: 0.14,
}

function getInterYearRates(make: string): InterYearRates {
  return MAKE_INTER_YEAR_RATES[make] || DEFAULT_INTER_YEAR_RATES
}

// ═══════════════════════════════════════
// RECENCY PREMIUM MULTIPLIER
// The last 2 model years command a disproportionate premium at auction
// because insurance payouts are higher and parts are more expensive.
//
// Multipliers applied per-year during compounding:
//   Oldest year (yearsFromBase = 0):  0.7x  → below-average appreciation
//   Middle years:                     1.0x  → baseline rate
//   Last 2 years (near totalSpan):    1.8x  → recency premium
//
// Applied year-by-year (not as a flat multiplier on the exponent)
// so each year's contribution is individually scaled.
// ═══════════════════════════════════════
function recencyMultiplier(
  yearIndex: number, // 1-based: which year step we're on
  totalSpan: number, // total years in the range
): number {
  if (totalSpan === 0) return 1.0
  if (yearIndex <= 1) return 0.7 // oldest year step
  if (yearIndex >= totalSpan) return 1.8 // last 2 year steps
  if (yearIndex >= totalSpan - 1) return 1.8
  return 1.0 // middle years
}

/**
 * Year-by-year compound growth with recency premium.
 * Replaces Math.pow(1 + rate, yearsFromBase) with a loop that
 * applies a per-year multiplier to the base rate.
 */
function applyYearlyRates(
  baseBid: number,
  rate: number,
  yearsFromBase: number,
  totalSpan: number,
): number {
  let bid = baseBid
  for (let y = 1; y <= yearsFromBase; y++) {
    const multiplier = recencyMultiplier(y, totalSpan)
    bid *= 1 + rate * multiplier
  }
  return Math.round(bid)
}

// ═══════════════════════════════════════
// MAKE-SPECIFIC CALIBRATION RATES
// What fraction of "listed" auction value real winning bids represent.
//
// Calibrated against real data:
//   Nissan Sentra SR 2021: actual bids $2,150–$3,300
//   Raw SR year-adjusted = [4009, 5850] → ratios: 0.536 / 0.564
//
// Factors driving the ratio:
//   min = heavily damaged (engine/frame/flood) — bidders cautious
//   max = lightly damaged — competitive bidding, closer to market value
//
// Luxury brands: lower ratio (high repair costs deter importers)
// Popular economy brands: higher ratio (high demand, aggressive bidding)
// ═══════════════════════════════════════
interface CalibrationRates {
  min: number // heavily damaged scenario
  max: number // lightly damaged scenario
}

const MAKE_CALIBRATION_RATES: Record<string, CalibrationRates> = {
  // ── Japanese (high demand, competitive bidding) ──
  Toyota: { min: 0.68, max: 0.76 },
  Honda: { min: 0.66, max: 0.74 },
  Mazda: { min: 0.56, max: 0.64 }, // unchanged — no real data point yet
  Subaru: { min: 0.57, max: 0.65 }, // unchanged — no real data point yet
  Nissan: { min: 0.62, max: 0.7 },

  // ── Korean ──
  Hyundai: { min: 0.68, max: 0.76 }, // Calibrated: 2024 Kona Limited $6,100 bid → $12,890 landed ✓
  Kia: { min: 0.68, max: 0.76 },

  // ── German ──
  Volkswagen: { min: 0.58, max: 0.66 },
  BMW: { min: 0.52, max: 0.6 },
  Audi: { min: 0.5, max: 0.58 },

  // ── American ──
  Ford: { min: 0.6, max: 0.68 },
  Chevrolet: { min: 0.6, max: 0.68 },
  Dodge: { min: 0.62, max: 0.7 },
}

const DEFAULT_CALIBRATION: CalibrationRates = { min: 0.52, max: 0.6 }

function getCalibration(make: string): CalibrationRates {
  return MAKE_CALIBRATION_RATES[make] || DEFAULT_CALIBRATION
}

// ═══════════════════════════════════════
// SAMPLED AVERAGE COST ENGINE
// Instead of using just min/max endpoints, sample every $100
// across the raw auction range and average the calculateTotalCost
// results at each calibration extreme.
//
// Why sampling matters:
//   calculateTotalCost() is NON-LINEAR (tiered IAAI fees, % taxes).
//   Using just two endpoints misses the curve. Sampling every $100
//   gives a true average of what the total cost distribution looks like.
//
// Sample step: $100 (max precision for typical $500–$25,000 ranges)
// ═══════════════════════════════════════
const SAMPLE_STEP = 100

function sampleCosts(
  rawAuctionMin: number,
  rawAuctionMax: number,
  calibration: CalibrationRates,
): { avgMin: number; avgMax: number } {
  // Generate sample points every $100 across the raw auction range
  const samples: number[] = []
  for (let bid = rawAuctionMin; bid <= rawAuctionMax; bid += SAMPLE_STEP) {
    samples.push(bid)
  }
  // Always include the max endpoint
  if (samples.length === 0 || samples[samples.length - 1] < rawAuctionMax) {
    samples.push(rawAuctionMax)
  }

  // For each sample: apply calibration and compute total landed cost
  // min scenario = each sample × calibration.min (damaged condition)
  // max scenario = each sample × calibration.max (good condition)
  const minCosts = samples.map((s) =>
    calculateTotalCost(Math.round(s * calibration.min)),
  )
  const maxCosts = samples.map((s) =>
    calculateTotalCost(Math.round(s * calibration.max)),
  )

  const avgMin =
    Math.round(minCosts.reduce((a, b) => a + b, 0) / minCosts.length / 100) *
    100

  const avgMax =
    Math.round(maxCosts.reduce((a, b) => a + b, 0) / maxCosts.length / 100) *
    100

  return { avgMin, avgMax: Math.max(avgMax, avgMin + 300) }
}

// ═══════════════════════════════════════
// CAR DATA BUILDER
// Stores auction bid estimates, computes total costs automatically
// ═══════════════════════════════════════
interface RawTrim {
  name: string
  auction: [number, number]
  features: string[]
}

interface RawCar {
  id: string
  make: string
  model: string
  yearRange: string
  auction: [number, number] // [min bid, max bid]
  type: CarType
  engine: string
  description: string
  trims: RawTrim[]
}

function buildCarData(raw: RawCar[]): CarModel[] {
  return raw.map((car) => {
    const years = getYearsFromRange(car.yearRange)
    const totalSpan = years.length > 1 ? years[years.length - 1] - years[0] : 0
    const rates = getInterYearRates(car.make)
    const calibration = getCalibration(car.make)

    // Overall price range: oldest-year damaged → newest-year good condition
    // Uses applyYearlyRates with recency premium for the newest year
    const newestMaxBid = applyYearlyRates(
      car.auction[1],
      rates.maxRate,
      totalSpan,
      totalSpan,
    )
    const { avgMin, avgMax } = sampleCosts(
      car.auction[0],
      newestMaxBid,
      calibration,
    )

    return {
      id: car.id,
      make: car.make,
      model: car.model,
      yearRange: car.yearRange,
      auctionRange: { min: car.auction[0], max: car.auction[1] },
      priceRange: { min: avgMin, max: avgMax },
      type: car.type,
      engine: car.engine,
      description: car.description,
      trims: car.trims.map((t) => {
        const { avgMin: tMin, avgMax: tMax } = sampleCosts(
          t.auction[0],
          t.auction[1],
          calibration,
        )
        return {
          name: t.name,
          auctionEstimate: t.auction as [number, number],
          priceEstimate: formatPriceRange(tMin, tMax),
          features: t.features,
        }
      }),
    }
  })
}

// ═══════════════════════════════════════
// RAW CAR DATA (auction bid estimates)
// Prices based on typical IAAI/Copart archived auction results
// for import-grade vehicles (run & drive, various conditions)
// ═══════════════════════════════════════
const rawCarData: RawCar[] = [
  // ═══════════════════════════════════════
  // TOYOTA
  // ═══════════════════════════════════════
  {
    id: 'corolla-11',
    make: 'Toyota',
    model: 'Corolla',
    yearRange: '2018-2019',
    auction: [1200, 2800],
    type: 'Sedán',
    engine: '1.8L 4cyl',
    description:
      'Generación anterior, probada y extremadamente confiable. Ideal para primer auto.',
    trims: [
      {
        name: 'L',
        auction: [1200, 1700],
        features: ['Básico', 'Confiable', 'Tela'],
      },
      {
        name: 'LE',
        auction: [1500, 2200],
        features: ['Cámara reversa', 'Pantalla 6.1"', 'Climatizador'],
      },
      {
        name: 'SE',
        auction: [2000, 2800],
        features: ['Deportivo', 'Rines 17"', 'Paddle Shifters'],
      },
    ],
  },
  {
    id: 'corolla-12',
    make: 'Toyota',
    model: 'Corolla',
    yearRange: '2020-2024',
    auction: [2800, 7000],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description:
      'Diseño totalmente renovado, más seguro y con mejor tecnología.',
    trims: [
      {
        name: 'L',
        auction: [2800, 4000],
        features: ['Apple CarPlay', 'Toyota Safety Sense 2.0', 'LEDs'],
      },
      {
        name: 'LE',
        auction: [3500, 5000],
        features: ['Pantalla 8"', 'Rines 16"', 'Climatizador auto'],
      },
      {
        name: 'SE',
        auction: [4200, 5800],
        features: ['Motor 2.0L', 'Suspensión deportiva', 'Rines 18"'],
      },
      {
        name: 'XSE',
        auction: [5500, 7000],
        features: ['Cuero', 'Sunroof', 'Asientos calefactados'],
      },
    ],
  },
  {
    id: 'camry-8',
    make: 'Toyota',
    model: 'Camry',
    yearRange: '2018-2024',
    auction: [2200, 8500],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description:
      'El sedán mediano más vendido. Confort, espacio y confiabilidad legendaria.',
    trims: [
      {
        name: 'LE',
        auction: [2200, 4500],
        features: ['Motor 2.5L', 'Safety Sense', 'CarPlay'],
      },
      {
        name: 'SE',
        auction: [3000, 5800],
        features: ['Suspensión deportiva', 'Rines 18"', 'Modo Sport'],
      },
      {
        name: 'XLE',
        auction: [4200, 7000],
        features: ['Cuero', 'Sunroof', 'Pantalla 9"'],
      },
      {
        name: 'XSE',
        auction: [5800, 8500],
        features: ['Motor V6 (opc)', 'Rines 19"', 'JBL Audio'],
      },
    ],
  },
  {
    id: 'prius-4',
    make: 'Toyota',
    model: 'Prius',
    yearRange: '2018-2022',
    auction: [2000, 5800],
    type: 'Hatchback',
    engine: '1.8L 4cyl Híbrido',
    description:
      'El híbrido más famoso del mundo. Eficiencia extrema y confiabilidad.',
    trims: [
      {
        name: 'L Eco',
        auction: [2000, 3200],
        features: ['Híbrido', '56 MPG', 'Safety Sense'],
      },
      {
        name: 'LE',
        auction: [2800, 4000],
        features: ['Pantalla 7"', 'CarPlay', 'Climatizador auto'],
      },
      {
        name: 'XLE',
        auction: [3500, 5000],
        features: ['Cuero SofTex', 'Sunroof', 'Head-up Display'],
      },
      {
        name: 'Limited',
        auction: [4200, 5800],
        features: ['Rines 17"', 'JBL Audio', 'Carga inalámbrica'],
      },
    ],
  },
  {
    id: 'prius-5',
    make: 'Toyota',
    model: 'Prius',
    yearRange: '2023-2024',
    auction: [7000, 13000],
    type: 'Hatchback',
    engine: '2.0L 4cyl Híbrido',
    description:
      'Rediseño radical. Ahora deportivo, potente y con diseño atractivo.',
    trims: [
      {
        name: 'LE',
        auction: [7000, 8500],
        features: ['Híbrido 194hp', 'Pantalla 8"', 'Safety Sense 3.0'],
      },
      {
        name: 'XLE',
        auction: [8500, 11000],
        features: ['Pantalla 12.3"', 'Cuero SofTex', 'Sunroof'],
      },
      {
        name: 'Limited',
        auction: [10500, 13000],
        features: ['Rines 19"', 'JBL Audio', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'chr-1',
    make: 'Toyota',
    model: 'C-HR',
    yearRange: '2018-2022',
    auction: [2000, 5500],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'Crossover compacto con diseño audaz y manejo ágil.',
    trims: [
      {
        name: 'LE',
        auction: [2000, 3200],
        features: ['Motor 2.0L', 'Safety Sense', 'Pantalla 8"'],
      },
      {
        name: 'XLE',
        auction: [2800, 4200],
        features: ['Rines 18"', 'Blind Spot', 'Climatizador dual'],
      },
      {
        name: 'Limited',
        auction: [3800, 5500],
        features: ['Cuero', 'JBL Audio', 'Carga inalámbrica'],
      },
    ],
  },
  {
    id: 'corollacross-1',
    make: 'Toyota',
    model: 'Corolla Cross',
    yearRange: '2022-2024',
    auction: [4800, 9500],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description:
      'Crossover basado en Corolla. Práctico, eficiente y confiable.',
    trims: [
      {
        name: 'L',
        auction: [4800, 6200],
        features: ['Motor 2.0L', 'Safety Sense', 'CarPlay'],
      },
      {
        name: 'LE',
        auction: [5800, 7800],
        features: ['Pantalla 8"', 'Blind Spot', 'Rines 17"'],
      },
      {
        name: 'XLE',
        auction: [7500, 9500],
        features: ['Cuero SofTex', 'Sunroof', 'Power Liftgate'],
      },
    ],
  },
  {
    id: 'rav4-4',
    make: 'Toyota',
    model: 'RAV4',
    yearRange: '2018',
    auction: [3000, 5000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'La última del diseño clásico. Espaciosa y muy confiable.',
    trims: [
      {
        name: 'LE',
        auction: [3000, 3800],
        features: ['Toyota Safety Sense', 'Rieles techo', 'Cámara'],
      },
      {
        name: 'XLE',
        auction: [3600, 4500],
        features: ['Sunroof', 'Climatizador', 'Rines aleación'],
      },
      {
        name: 'Limited',
        auction: [4200, 5000],
        features: ['Cuero', 'Navegación', 'Memoria asientos'],
      },
    ],
  },
  {
    id: 'rav4-5',
    make: 'Toyota',
    model: 'RAV4',
    yearRange: '2019-2024',
    auction: [5500, 13000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'Estilo robusto, plataforma TNGA y excelente reventa.',
    trims: [
      {
        name: 'LE',
        auction: [5500, 8000],
        features: ['LEDs', 'CarPlay', 'Safety Sense 2.0'],
      },
      {
        name: 'XLE',
        auction: [7000, 9500],
        features: ['Rines 17"', 'Smart Key', 'Asiento eléctrico'],
      },
      {
        name: 'XLE Premium',
        auction: [8500, 11000],
        features: ['Rines 19"', 'SofTex', 'Power Liftgate'],
      },
      {
        name: 'Limited',
        auction: [10500, 13000],
        features: ['Cámaras 360', 'Asientos ventilados', 'JBL Audio'],
      },
    ],
  },
  {
    id: 'highlander-4',
    make: 'Toyota',
    model: 'Highlander',
    yearRange: '2020-2024',
    auction: [8000, 17000],
    type: 'SUV',
    engine: '3.5L V6',
    description:
      'SUV familiar de 3 filas. Confiable, espaciosa y con excelente reventa.',
    trims: [
      {
        name: 'L',
        auction: [8000, 10500],
        features: ['Motor 3.5L V6', 'Safety Sense', 'CarPlay'],
      },
      {
        name: 'LE',
        auction: [9500, 12000],
        features: ['Pantalla 8"', 'Rines 18"', 'Climatizador 3 zonas'],
      },
      {
        name: 'XLE',
        auction: [11000, 14500],
        features: ['Cuero SofTex', 'Sunroof', 'Power Liftgate'],
      },
      {
        name: 'Limited',
        auction: [14000, 17000],
        features: ['Cuero', 'JBL Audio', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'highlander-3',
    make: 'Toyota',
    model: 'Highlander',
    yearRange: '2018-2019',
    auction: [4200, 8000],
    type: 'SUV',
    engine: '3.5L V6',
    description: 'Generación anterior de 3 filas. Probada y confiable.',
    trims: [
      {
        name: 'LE',
        auction: [4200, 5500],
        features: ['Motor 3.5L V6', 'Safety Sense', 'Cámara'],
      },
      {
        name: 'XLE',
        auction: [5500, 6800],
        features: ['Sunroof', 'Cuero', 'Rines 18"'],
      },
      {
        name: 'Limited',
        auction: [6800, 8000],
        features: ['Navegación', 'JBL Audio', 'Ventilados'],
      },
    ],
  },
  {
    id: '4runner-5',
    make: 'Toyota',
    model: '4Runner',
    yearRange: '2018-2024',
    auction: [8000, 18000],
    type: 'SUV',
    engine: '4.0L V6',
    description: 'SUV body-on-frame legendaria. Capacidad off-road real.',
    trims: [
      {
        name: 'SR5',
        auction: [8000, 11500],
        features: ['Motor 4.0L V6', 'Pantalla 8"', 'Safety Sense'],
      },
      {
        name: 'SR5 Premium',
        auction: [10500, 14000],
        features: ['Cuero SofTex', 'Sunroof', 'Rines 17"'],
      },
      {
        name: 'TRD Off-Road',
        auction: [12000, 15500],
        features: ['Bloqueo diferencial', 'Crawl Control', 'KDSS'],
      },
      {
        name: 'Limited',
        auction: [15000, 18000],
        features: ['Cuero', 'JBL Audio', 'Rines 20"'],
      },
    ],
  },
  {
    id: 'tacoma-3',
    make: 'Toyota',
    model: 'Tacoma',
    yearRange: '2018-2023',
    auction: [7000, 16000],
    type: 'Pick-up',
    engine: '3.5L V6',
    description: 'Valor de reventa inigualable. Capacidad off-road legendaria.',
    trims: [
      {
        name: 'SR5',
        auction: [7000, 10000],
        features: ['Pantalla táctil', 'Safety Sense', 'Rines aleación'],
      },
      {
        name: 'TRD Sport',
        auction: [9000, 12500],
        features: ['Hood scoop', 'Rines 17"', 'Suspensión deportiva'],
      },
      {
        name: 'TRD Off-Road',
        auction: [10500, 14000],
        features: ['Bloqueo diferencial', 'Bilstein', 'Crawl Control'],
      },
      {
        name: 'Limited',
        auction: [13000, 16000],
        features: ['Cuero', 'Sunroof', 'JBL Audio'],
      },
    ],
  },
  {
    id: 'tundra-3',
    make: 'Toyota',
    model: 'Tundra',
    yearRange: '2022-2024',
    auction: [13000, 23000],
    type: 'Pick-up',
    engine: '3.5L V6 Twin Turbo',
    description:
      'Pick-up full-size totalmente nueva. Motor twin-turbo V6 potente.',
    trims: [
      {
        name: 'SR5',
        auction: [13000, 16000],
        features: ['Motor 3.5TT V6', 'Pantalla 8"', 'Safety Sense'],
      },
      {
        name: 'Limited',
        auction: [16000, 19000],
        features: ['Cuero', 'Pantalla 14"', 'JBL Audio'],
      },
      {
        name: '1794 Edition',
        auction: [18500, 21000],
        features: ['Cuero premium', 'Sunroof', 'Cámaras 360'],
      },
      {
        name: 'TRD Pro',
        auction: [20000, 23000],
        features: ['Fox shocks', 'Skid plates', 'TRD exhaust'],
      },
    ],
  },
  {
    id: 'tundra-2',
    make: 'Toyota',
    model: 'Tundra',
    yearRange: '2018-2021',
    auction: [7000, 13000],
    type: 'Pick-up',
    engine: '5.7L V8',
    description: 'Pick-up full-size con motor V8 iForce probado y confiable.',
    trims: [
      {
        name: 'SR5',
        auction: [7000, 9000],
        features: ['Motor 5.7L V8', 'Pantalla táctil', 'Safety Sense'],
      },
      {
        name: 'Limited',
        auction: [9000, 11000],
        features: ['Cuero', 'Sunroof', 'JBL Audio'],
      },
      {
        name: 'Platinum',
        auction: [10500, 13000],
        features: ['Cuero premium', 'Ventilados', 'Cámaras 360'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // HONDA
  // ═══════════════════════════════════════
  {
    id: 'civic-10',
    make: 'Honda',
    model: 'Civic',
    yearRange: '2018-2021',
    auction: [2000, 5500],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description:
      'Diseño agresivo, manejo deportivo y excelente espacio interior.',
    trims: [
      {
        name: 'LX',
        auction: [2000, 3000],
        features: ['Motor 2.0L', 'Cámara reversa', 'Honda Sensing (2019+)'],
      },
      {
        name: 'Sport',
        auction: [2800, 4000],
        features: ['Escape central', 'Rines 18"', 'CarPlay'],
      },
      {
        name: 'EX',
        auction: [3300, 4500],
        features: ['Motor 1.5T', 'Sunroof', 'LaneWatch'],
      },
      {
        name: 'Touring',
        auction: [4200, 5500],
        features: ['Cuero', 'Navegación', 'LEDs Premium'],
      },
    ],
  },
  {
    id: 'civic-11',
    make: 'Honda',
    model: 'Civic',
    yearRange: '2022-2024',
    auction: [5500, 11000],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description: 'Más maduro, minimalista y con tecnología de punta.',
    trims: [
      {
        name: 'LX',
        auction: [5500, 7000],
        features: ['Pantalla 7"', 'LEDs', 'Push Button Start'],
      },
      {
        name: 'Sport',
        auction: [6500, 8200],
        features: ['Rines negros 18"', 'Modo Sport', 'Volante cuero'],
      },
      {
        name: 'EX',
        auction: [7800, 9500],
        features: ['Motor 1.5T', 'Sunroof', 'Blind Spot Info'],
      },
      {
        name: 'Touring',
        auction: [9500, 11000],
        features: ['Pantalla 9"', 'Bose Audio', 'Wireless CarPlay'],
      },
    ],
  },
  {
    id: 'accord-10',
    make: 'Honda',
    model: 'Accord',
    yearRange: '2018-2024',
    auction: [3000, 10000],
    type: 'Sedán',
    engine: '1.5L 4cyl Turbo',
    description:
      'Sedán mediano premium. Excelente manejo, espacio y tecnología.',
    trims: [
      {
        name: 'LX',
        auction: [3000, 5000],
        features: ['Motor 1.5T', 'Honda Sensing', 'CarPlay'],
      },
      {
        name: 'Sport',
        auction: [4200, 6500],
        features: ['Rines 19"', 'Escape doble', 'Volante cuero'],
      },
      {
        name: 'EX-L',
        auction: [5800, 8200],
        features: ['Cuero', 'Sunroof', 'Memoria asientos'],
      },
      {
        name: 'Touring',
        auction: [7800, 10000],
        features: ['Motor 2.0T', 'Head-up Display', 'Navegación'],
      },
    ],
  },
  {
    id: 'fit-3',
    make: 'Honda',
    model: 'Fit',
    yearRange: '2018-2020',
    auction: [1400, 3200],
    type: 'Hatchback',
    engine: '1.5L 4cyl',
    description:
      'Subcompacto mágico. Espacio interior increíble para su tamaño.',
    trims: [
      {
        name: 'LX',
        auction: [1400, 2000],
        features: ['Motor 1.5L', 'Cámara reversa', 'Bluetooth'],
      },
      {
        name: 'Sport',
        auction: [1800, 2400],
        features: ['Rines 16"', 'Paddle Shifters', 'Spoiler'],
      },
      {
        name: 'EX',
        auction: [2200, 2800],
        features: ['Sunroof', 'Honda Sensing', 'CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [2600, 3200],
        features: ['Cuero', 'Navegación', 'Asientos calefactados'],
      },
    ],
  },
  {
    id: 'hrv-2',
    make: 'Honda',
    model: 'HR-V',
    yearRange: '2018-2022',
    auction: [2200, 5500],
    type: 'SUV',
    engine: '1.8L 4cyl',
    description: 'Crossover subcompacto versátil con asientos Magic Seat.',
    trims: [
      {
        name: 'LX',
        auction: [2200, 3200],
        features: ['Motor 1.8L', 'Cámara reversa', 'Bluetooth'],
      },
      {
        name: 'Sport',
        auction: [2800, 4000],
        features: ['Rines 18"', 'Pantalla 7"', 'Volante cuero'],
      },
      {
        name: 'EX',
        auction: [3500, 4800],
        features: ['Sunroof', 'Honda Sensing', 'CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [4200, 5500],
        features: ['Cuero', 'Navegación', 'Asientos calefactados'],
      },
    ],
  },
  {
    id: 'hrv-3',
    make: 'Honda',
    model: 'HR-V',
    yearRange: '2023-2024',
    auction: [5500, 9000],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description:
      'Totalmente rediseñada. Más grande, más premium y con motor turbo.',
    trims: [
      {
        name: 'LX',
        auction: [5500, 6800],
        features: ['Motor 2.0L', 'Pantalla 7"', 'Honda Sensing'],
      },
      {
        name: 'Sport',
        auction: [6500, 7800],
        features: ['Rines 18"', 'Pantalla 9"', 'Wireless CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [7500, 9000],
        features: ['Cuero', 'Sunroof', 'Bose Audio'],
      },
    ],
  },
  {
    id: 'crv-5',
    make: 'Honda',
    model: 'CR-V',
    yearRange: '2018-2022',
    auction: [3500, 8000],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'La referencia en espacio y practicidad familiar.',
    trims: [
      {
        name: 'LX',
        auction: [3500, 4800],
        features: ['Motor 2.4L', 'Rines 17"', 'Climatizador'],
      },
      {
        name: 'EX',
        auction: [4200, 5800],
        features: ['Motor 1.5T', 'Sunroof', 'CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [5500, 7000],
        features: ['Cuero', 'Power Tailgate', 'Memoria asientos'],
      },
      {
        name: 'Touring',
        auction: [6500, 8000],
        features: ['Navegación', 'LEDs', 'Escape doble'],
      },
    ],
  },
  {
    id: 'crv-6',
    make: 'Honda',
    model: 'CR-V',
    yearRange: '2023-2024',
    auction: [8500, 14000],
    type: 'SUV',
    engine: '1.5L 4cyl',
    description: 'Más grande, más premium y con diseño robusto.',
    trims: [
      {
        name: 'EX',
        auction: [8500, 10500],
        features: ['Motor 1.5T', 'Honda Sensing', 'Rines 18"'],
      },
      {
        name: 'EX-L',
        auction: [10500, 12000],
        features: ['Pantalla 9"', 'Wireless CarPlay', 'Cuero'],
      },
      {
        name: 'Sport Touring',
        auction: [12000, 14000],
        features: ['Híbrido', 'Rines negros 19"', 'Bose'],
      },
    ],
  },
  {
    id: 'pilot-4',
    make: 'Honda',
    model: 'Pilot',
    yearRange: '2019-2024',
    auction: [6500, 15000],
    type: 'SUV',
    engine: '3.5L V6',
    description: 'SUV de 3 filas familiar. Espaciosa, cómoda y bien equipada.',
    trims: [
      {
        name: 'LX',
        auction: [6500, 9000],
        features: ['Motor 3.5L V6', 'Honda Sensing', 'CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [8500, 11500],
        features: ['Cuero', 'Sunroof', 'Power Tailgate'],
      },
      {
        name: 'Touring',
        auction: [10500, 13000],
        features: ['Navegación', 'Rear Entertainment', 'Ventilados'],
      },
      {
        name: 'Elite/Black Edition',
        auction: [12500, 15000],
        features: ['Rines 20"', 'Wireless CarPlay', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'passport-1',
    make: 'Honda',
    model: 'Passport',
    yearRange: '2019-2024',
    auction: [5500, 12000],
    type: 'SUV',
    engine: '3.5L V6',
    description:
      'SUV mediana aventurera. Más robusta que CR-V, más ágil que Pilot.',
    trims: [
      {
        name: 'Sport',
        auction: [5500, 8000],
        features: ['Motor 3.5L V6', 'Honda Sensing', 'Rines 20"'],
      },
      {
        name: 'EX-L',
        auction: [7500, 10000],
        features: ['Cuero', 'Sunroof', 'Power Tailgate'],
      },
      {
        name: 'Touring',
        auction: [9500, 12000],
        features: ['Navegación', 'Ventilados', 'Wireless CarPlay'],
      },
    ],
  },
  {
    id: 'ridgeline-2',
    make: 'Honda',
    model: 'Ridgeline',
    yearRange: '2018-2024',
    auction: [5500, 13000],
    type: 'Pickup',
    engine: '3.5L V6',
    description:
      'Pick-up unibody única. Manejo de auto, utilidad de camioneta.',
    trims: [
      {
        name: 'Sport',
        auction: [5500, 8000],
        features: ['Motor 3.5L V6', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'RTL',
        auction: [7500, 10000],
        features: ['Cuero', 'Sunroof', 'Power Tailgate'],
      },
      {
        name: 'RTL-E',
        auction: [9200, 11500],
        features: ['Navegación', 'Bed Audio', 'Ventilados'],
      },
      {
        name: 'Black Edition',
        auction: [10500, 13000],
        features: ['Rines negros', 'Interior rojo/negro', 'Wireless CarPlay'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // HYUNDAI
  // ═══════════════════════════════════════
  {
    id: 'accent-5',
    make: 'Hyundai',
    model: 'Accent',
    yearRange: '2018-2022',
    auction: [600, 2000],
    type: 'Sedán',
    engine: '1.6L 4cyl',
    description:
      'Subcompacto económico. Ideal para primer auto o presupuesto ajustado.',
    trims: [
      {
        name: 'SE',
        auction: [600, 1200],
        features: ['Motor 1.6L', 'Pantalla 5"', 'Bluetooth'],
      },
      {
        name: 'SEL',
        auction: [1000, 1600],
        features: ['Pantalla 7"', 'CarPlay', 'Cámara reversa'],
      },
      {
        name: 'Limited',
        auction: [1400, 2000],
        features: ['Sunroof', 'Cuero parcial', 'Push Start'],
      },
    ],
  },
  {
    id: 'elantra-6',
    make: 'Hyundai',
    model: 'Elantra',
    yearRange: '2018-2020',
    auction: [900, 2500],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description: 'Diseño angular y excelente equipamiento por el precio.',
    trims: [
      {
        name: 'SE',
        auction: [900, 1600],
        features: ['Cámara reversa', 'Bluetooth', 'Modos de manejo'],
      },
      {
        name: 'SEL',
        auction: [1400, 2000],
        features: ['Pantalla 7"', 'CarPlay', 'Blind Spot'],
      },
      {
        name: 'Limited',
        auction: [1800, 2500],
        features: ['Cuero', 'LEDs', 'Carga inalámbrica'],
      },
    ],
  },
  {
    id: 'elantra-7',
    make: 'Hyundai',
    model: 'Elantra',
    yearRange: '2021-2024',
    auction: [2800, 6200],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description:
      'Diseño futurista "Parametric Dynamics" y tecnología avanzada.',
    trims: [
      {
        name: 'SE',
        auction: [2800, 4000],
        features: ['Pantalla 8"', 'Wireless CarPlay', 'Frenado auto'],
      },
      {
        name: 'SEL',
        auction: [3500, 5000],
        features: ['Llave inteligente', 'Climatizador dual', 'BlueLink'],
      },
      {
        name: 'Limited',
        auction: [4800, 6200],
        features: ['Pantallas duales 10.25"', 'Bose', 'Cuero'],
      },
    ],
  },
  {
    id: 'sonata-7',
    make: 'Hyundai',
    model: 'Sonata',
    yearRange: '2018-2019',
    auction: [1500, 3500],
    type: 'Sedán',
    engine: '2.4L 4cyl',
    description: 'Sedán mediano espacioso con buen equipamiento.',
    trims: [
      {
        name: 'SE',
        auction: [1500, 2200],
        features: ['Motor 2.4L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'SEL',
        auction: [2000, 2800],
        features: ['Blind Spot', 'Asientos calefactados', 'Push Start'],
      },
      {
        name: 'Limited',
        auction: [2600, 3500],
        features: ['Cuero', 'Sunroof', 'Infinity Audio'],
      },
    ],
  },
  {
    id: 'sonata-8',
    make: 'Hyundai',
    model: 'Sonata',
    yearRange: '2020-2024',
    auction: [3000, 7800],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Diseño dramático y tecnología de clase superior.',
    trims: [
      {
        name: 'SE',
        auction: [3000, 4800],
        features: ['Motor 2.5L', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'SEL',
        auction: [4200, 6200],
        features: ['Sunroof', 'Asientos calefactados', 'BlueLink'],
      },
      {
        name: 'Limited',
        auction: [5800, 7800],
        features: ['Cuero Nappa', 'Bose', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'venue-1',
    make: 'Hyundai',
    model: 'Venue',
    yearRange: '2020-2024',
    auction: [1500, 3500],
    type: 'SUV',
    engine: '1.6L 4cyl',
    description: 'SUV subcompacta urbana. Económica y fácil de manejar.',
    trims: [
      {
        name: 'SE',
        auction: [1500, 2200],
        features: ['Motor 1.6L', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'SEL',
        auction: [2200, 2800],
        features: ['Blind Spot', 'Asientos calefactados', 'Push Start'],
      },
      {
        name: 'Limited',
        auction: [2800, 3500],
        features: ['Cuero parcial', 'Sunroof', 'Bose'],
      },
    ],
  },
  {
    id: 'kona-2',
    make: 'Hyundai',
    model: 'Kona',
    yearRange: '2018-2021',
    auction: [2200, 5500],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description:
      'Crossover subcompacto con personalidad. Ágil y bien equipado.',
    trims: [
      {
        name: 'SE',
        auction: [2200, 3200],
        features: ['Motor 2.0L', 'Pantalla 7"', 'Safety Tech'],
      },
      {
        name: 'SEL',
        auction: [2800, 4000],
        features: ['Blind Spot', 'Asientos calefactados', 'Rines 17"'],
      },
      {
        name: 'Limited',
        auction: [4000, 5500],
        features: ['Cuero', 'Sunroof', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'kona-3',
    make: 'Hyundai',
    model: 'Kona',
    yearRange: '2022-2024',
    auction: [3800, 6800],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'Kona actualizada con N Line deportivo y más tecnología.',
    trims: [
      {
        name: 'SE',
        auction: [3800, 4800],
        features: ['Motor 2.0L', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'SEL',
        auction: [4500, 5500],
        features: ['Blind Spot', 'Asientos calefactados', 'Rines 17"'],
      },
      {
        name: 'N Line',
        auction: [5200, 6000],
        features: ['Motor 1.6T', 'Rines 18"', 'Interior deportivo'],
      },
      {
        name: 'Limited',
        auction: [5800, 6800],
        features: ['Cuero', 'Sunroof', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'tucson-3',
    make: 'Hyundai',
    model: 'Tucson',
    yearRange: '2018-2021',
    auction: [2200, 5800],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'SUV compacta confiable con buen espacio y equipamiento.',
    trims: [
      {
        name: 'SE',
        auction: [2200, 3200],
        features: ['Motor 2.0L', 'Pantalla 7"', 'Blind Spot'],
      },
      {
        name: 'SEL',
        auction: [3000, 4200],
        features: ['Rines 17"', 'Asientos calefactados', 'CarPlay'],
      },
      {
        name: 'Limited',
        auction: [4200, 5800],
        features: ['Cuero', 'Techo panorámico', 'Infinity Audio'],
      },
    ],
  },
  {
    id: 'tucson-4',
    make: 'Hyundai',
    model: 'Tucson',
    yearRange: '2022-2024',
    auction: [5500, 10500],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'Diseño futurista, interior premium y opción híbrida.',
    trims: [
      {
        name: 'SE',
        auction: [5500, 7000],
        features: ['Pantalla 8"', 'Safety Tech', 'Rines 17"'],
      },
      {
        name: 'SEL',
        auction: [6800, 8800],
        features: ['Pantalla 10.25"', 'Llave digital', 'Blind Spot'],
      },
      {
        name: 'Limited',
        auction: [8500, 10500],
        features: ['Cuero', 'Techo panorámico', 'Bose'],
      },
    ],
  },
  {
    id: 'santafe-5',
    make: 'Hyundai',
    model: 'Santa Fe',
    yearRange: '2019-2024',
    auction: [3800, 10500],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'SUV familiar con 3 filas opcional. Potente y bien equipada.',
    trims: [
      {
        name: 'SE',
        auction: [3800, 5800],
        features: ['Motor 2.5L', 'Safety Tech', 'CarPlay'],
      },
      {
        name: 'SEL',
        auction: [5500, 8000],
        features: ['Rines 18"', 'Asientos calefactados', 'BlueLink'],
      },
      {
        name: 'Limited',
        auction: [8000, 10500],
        features: ['Cuero', 'Techo panorámico', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'palisade-1',
    make: 'Hyundai',
    model: 'Palisade',
    yearRange: '2020-2024',
    auction: [9000, 17500],
    type: 'SUV',
    engine: '3.8L V6',
    description:
      'SUV grande premium de 3 filas. Compite con lujo a precio accesible.',
    trims: [
      {
        name: 'SE',
        auction: [9000, 11500],
        features: ['Motor 3.8L V6', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'SEL',
        auction: [11000, 13500],
        features: ['Cuero', 'Sunroof', 'Asientos calefactados 2 filas'],
      },
      {
        name: 'Limited',
        auction: [13000, 15500],
        features: ['Cuero Nappa', 'Harman Kardon', 'Head-up Display'],
      },
      {
        name: 'Calligraphy',
        auction: [15000, 17500],
        features: ['Quilted Nappa', 'Rines 20"', 'Intercom'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // KIA
  // ═══════════════════════════════════════
  {
    id: 'rio-4',
    make: 'Kia',
    model: 'Rio',
    yearRange: '2018-2023',
    auction: [700, 2200],
    type: 'Sedán',
    engine: '1.6L 4cyl',
    description: 'Subcompacto económico con buena garantía y equipamiento.',
    trims: [
      {
        name: 'LX',
        auction: [700, 1200],
        features: ['Motor 1.6L', 'Pantalla 5"', 'Bluetooth'],
      },
      {
        name: 'S',
        auction: [1000, 1700],
        features: ['Pantalla 7"', 'CarPlay', 'Cámara reversa'],
      },
      {
        name: 'EX',
        auction: [1500, 2200],
        features: ['Sunroof', 'Push Start', 'Rines 16"'],
      },
    ],
  },
  {
    id: 'forte-3',
    make: 'Kia',
    model: 'Forte',
    yearRange: '2019-2024',
    auction: [1700, 4800],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description: 'Sedán compacto con excelente equipamiento y garantía líder.',
    trims: [
      {
        name: 'FE',
        auction: [1700, 2800],
        features: ['Motor 2.0L', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'LXS',
        auction: [2400, 3500],
        features: ['Rines 16"', 'Asiento conductor eléctrico', 'Blind Spot'],
      },
      {
        name: 'GT-Line',
        auction: [3200, 4200],
        features: ['Rines 18"', 'Interior deportivo', 'LEDs'],
      },
      {
        name: 'GT',
        auction: [3800, 4800],
        features: ['Motor 1.6T', 'Suspensión independiente', 'Cuero'],
      },
    ],
  },
  {
    id: 'optima-4',
    make: 'Kia',
    model: 'Optima',
    yearRange: '2018-2020',
    auction: [1500, 3800],
    type: 'Sedán',
    engine: '2.4L 4cyl',
    description: 'Sedán mediano elegante. Predecesor del K5.',
    trims: [
      {
        name: 'LX',
        auction: [1500, 2200],
        features: ['Motor 2.4L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'S',
        auction: [2000, 2800],
        features: ['Rines 18"', 'Interior deportivo', 'Push Start'],
      },
      {
        name: 'EX',
        auction: [2600, 3400],
        features: ['Cuero', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SX',
        auction: [3200, 3800],
        features: ['Motor 2.0T', 'Rines 18"', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'k5-1',
    make: 'Kia',
    model: 'K5',
    yearRange: '2021-2024',
    auction: [3800, 9000],
    type: 'Sedán',
    engine: '1.6L 4cyl Turbo',
    description: 'Reemplazo del Optima. Diseño audaz y manejo deportivo.',
    trims: [
      {
        name: 'LXS',
        auction: [3800, 5500],
        features: ['Motor 1.6T', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'GT-Line',
        auction: [5000, 6800],
        features: ['Rines 18"', 'Interior deportivo', 'Techo panorámico'],
      },
      {
        name: 'EX',
        auction: [6200, 7800],
        features: ['Cuero', 'Asientos ventilados', 'Bose'],
      },
      {
        name: 'GT',
        auction: [7500, 9000],
        features: ['Motor 2.5T', 'Rines 19"', 'Launch Control'],
      },
    ],
  },
  {
    id: 'niro-2',
    make: 'Kia',
    model: 'Niro',
    yearRange: '2023-2024',
    auction: [5500, 9000],
    type: 'SUV',
    engine: '1.6L 4cyl Híbrido',
    description: 'Crossover híbrido eficiente con diseño moderno.',
    trims: [
      {
        name: 'LX',
        auction: [5500, 6800],
        features: ['Híbrido', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'EX',
        auction: [6800, 8000],
        features: ['Pantalla 10.25"', 'Cuero sintético', 'Sunroof'],
      },
      {
        name: 'SX Touring',
        auction: [7800, 9000],
        features: ['Harman Kardon', 'Head-up Display', 'Ventilados'],
      },
    ],
  },
  {
    id: 'seltos-1',
    make: 'Kia',
    model: 'Seltos',
    yearRange: '2021-2024',
    auction: [3500, 7000],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'SUV subcompacta con mucho estilo y tecnología por el precio.',
    trims: [
      {
        name: 'LX',
        auction: [3500, 4500],
        features: ['Motor 2.0L', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'S',
        auction: [4200, 5500],
        features: ['Rines 17"', 'Sunroof', 'LED DRL'],
      },
      {
        name: 'EX',
        auction: [4800, 6200],
        features: ['Cuero sintético', 'Blind Spot', 'Carga inalámbrica'],
      },
      {
        name: 'SX Turbo',
        auction: [5800, 7000],
        features: ['Motor 1.6T', 'Rines 18"', 'Bose'],
      },
    ],
  },
  {
    id: 'sportage-4',
    make: 'Kia',
    model: 'Sportage',
    yearRange: '2018-2022',
    auction: [2200, 6500],
    type: 'SUV',
    engine: '2.4L 4cyl',
    description: 'SUV compacta probada con buen espacio y equipamiento.',
    trims: [
      {
        name: 'LX',
        auction: [2200, 3500],
        features: ['Motor 2.4L', 'Pantalla 7"', 'Cámara reversa'],
      },
      {
        name: 'S',
        auction: [3000, 4500],
        features: ['Rines 18"', 'CarPlay', 'Blind Spot'],
      },
      {
        name: 'EX',
        auction: [4000, 5500],
        features: ['Cuero', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SX Turbo',
        auction: [5000, 6500],
        features: ['Motor 2.0T', 'Rines 19"', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'sportage-5',
    make: 'Kia',
    model: 'Sportage',
    yearRange: '2023-2024',
    auction: [6800, 13000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description:
      'Diseño revolucionario con pantalla curva dual y mucho espacio.',
    trims: [
      {
        name: 'LX',
        auction: [6800, 8500],
        features: ['Pantalla dual 12.3"', 'Safety Tech', 'Rines 17"'],
      },
      {
        name: 'EX',
        auction: [8000, 10000],
        features: ['Cuero sintético', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SX',
        auction: [9500, 11500],
        features: ['Motor 2.5T', 'Rines 19"', 'Harman Kardon'],
      },
      {
        name: 'SX Prestige',
        auction: [11000, 13000],
        features: ['Cuero Nappa', 'Asientos ventilados', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'sorento-4',
    make: 'Kia',
    model: 'Sorento',
    yearRange: '2021-2024',
    auction: [5500, 12000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'SUV mediana de 3 filas con opciones turbo e híbrida.',
    trims: [
      {
        name: 'LX',
        auction: [5500, 7500],
        features: ['Motor 2.5L', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'S',
        auction: [7000, 9000],
        features: ['Rines 18"', 'Cuero sintético', 'Sunroof'],
      },
      {
        name: 'EX',
        auction: [8500, 10500],
        features: ['Motor 2.5T', 'Cuero', 'Bose'],
      },
      {
        name: 'SX',
        auction: [10000, 12000],
        features: ['Rines 20"', 'Ventilados', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'telluride-1',
    make: 'Kia',
    model: 'Telluride',
    yearRange: '2020-2024',
    auction: [9000, 17500],
    type: 'SUV',
    engine: '3.8L V6',
    description:
      'SUV grande premium de 3 filas. Ganadora de múltiples premios.',
    trims: [
      {
        name: 'LX',
        auction: [9000, 11500],
        features: ['Motor 3.8L V6', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'S',
        auction: [11000, 13500],
        features: ['Rines 20"', 'Cuero sintético', 'Sunroof'],
      },
      {
        name: 'EX',
        auction: [13000, 15500],
        features: ['Cuero', 'Harman Kardon', 'Asientos calefactados 2 filas'],
      },
      {
        name: 'SX',
        auction: [15000, 17500],
        features: ['Cuero Nappa', 'Head-up Display', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'soul-3',
    make: 'Kia',
    model: 'Soul',
    yearRange: '2020-2024',
    auction: [2000, 4800],
    type: 'Hatchback',
    engine: '2.0L 4cyl',
    description:
      'Diseño único y personalidad propia. Muy espacioso por dentro.',
    trims: [
      {
        name: 'LX',
        auction: [2000, 2800],
        features: ['Motor 2.0L', 'Pantalla 7"', 'Safety Tech'],
      },
      {
        name: 'S',
        auction: [2500, 3400],
        features: ['Rines 16"', 'CarPlay', 'Cámara reversa'],
      },
      {
        name: 'EX',
        auction: [3200, 4000],
        features: ['Sunroof', 'Cuero sintético', 'Harman Kardon'],
      },
      {
        name: 'Turbo',
        auction: [3800, 4800],
        features: ['Motor 1.6T', 'Rines 18"', 'Head-up Display'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // NISSAN
  // ═══════════════════════════════════════
  {
    id: 'versa-3',
    make: 'Nissan',
    model: 'Versa',
    yearRange: '2018-2019',
    auction: [300, 900],
    type: 'Sedán',
    engine: '1.6L 4cyl',
    description: 'El sedán más económico del mercado. Básico pero funcional.',
    trims: [
      {
        name: 'S',
        auction: [300, 500],
        features: ['Motor 1.6L', 'Bluetooth', 'Básico'],
      },
      {
        name: 'SV',
        auction: [450, 750],
        features: ['Pantalla 7"', 'Cámara reversa', 'Crucero'],
      },
      {
        name: 'SR',
        auction: [600, 900],
        features: ['Rines 16"', 'Spoiler', 'Interior deportivo'],
      },
    ],
  },
  {
    id: 'versa-4',
    make: 'Nissan',
    model: 'Versa',
    yearRange: '2020-2024',
    auction: [1400, 3500],
    type: 'Sedán',
    engine: '1.6L 4cyl',
    description: 'Totalmente rediseñado. Mucho mejor calidad y seguridad.',
    trims: [
      {
        name: 'S',
        auction: [1400, 2000],
        features: ['Motor 1.6L', 'Safety Shield 360', 'CarPlay'],
      },
      {
        name: 'SV',
        auction: [1800, 2800],
        features: ['Pantalla 7"', 'Push Start', 'Blind Spot'],
      },
      {
        name: 'SR',
        auction: [2500, 3500],
        features: ['Rines 17"', 'LEDs', 'Interior deportivo'],
      },
    ],
  },
  {
    id: 'sentra-b17',
    make: 'Nissan',
    model: 'Sentra',
    yearRange: '2018-2019',
    auction: [700, 1700],
    type: 'Sedán',
    engine: '1.8L 4cyl',
    description:
      'Opción económica y espaciosa. Ideal para presupuesto ajustado.',
    trims: [
      {
        name: 'S',
        auction: [700, 1000],
        features: ['Básico', 'Económico', 'Bluetooth'],
      },
      {
        name: 'SV',
        auction: [1000, 1400],
        features: ['Pantalla 5"', 'Llave inteligente', 'Rines 16"'],
      },
      {
        name: 'SR',
        auction: [1300, 1700],
        features: ['Deportivo', 'Rines 17"', 'Spoiler'],
      },
    ],
  },
  {
    id: 'sentra-b18',
    make: 'Nissan',
    model: 'Sentra',
    yearRange: '2020-2024',
    auction: [2000, 5000],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description:
      'Salto gigante en calidad. Diseño atractivo y mucha seguridad.',
    trims: [
      {
        name: 'S',
        auction: [2000, 3000],
        features: ['Safety Shield 360', 'Pantalla 7"', 'Push Start'],
      },
      {
        name: 'SV',
        auction: [2800, 4000],
        features: ['Pantalla 8"', 'CarPlay', 'Control Crucero Adaptativo'],
      },
      {
        name: 'SR',
        auction: [3800, 5000],
        features: ['Rines 18"', 'Luces LED', 'Interior deportivo'],
      },
    ],
  },
  {
    id: 'altima-6',
    make: 'Nissan',
    model: 'Altima',
    yearRange: '2019-2024',
    auction: [2800, 7000],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Sedán mediano con AWD disponible y motor turbo eficiente.',
    trims: [
      {
        name: 'S',
        auction: [2800, 4000],
        features: ['Motor 2.5L', 'Safety Shield 360', 'Pantalla 8"'],
      },
      {
        name: 'SV',
        auction: [3500, 5000],
        features: ['ProPILOT Assist', 'Rines 17"', 'Asientos calefactados'],
      },
      {
        name: 'SL',
        auction: [4800, 6000],
        features: ['Cuero', 'Bose', 'Sunroof'],
      },
      {
        name: 'Platinum',
        auction: [5800, 7000],
        features: ['Rines 19"', 'Cuero semi-anilina', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'maxima-8',
    make: 'Nissan',
    model: 'Maxima',
    yearRange: '2018-2023',
    auction: [3000, 7800],
    type: 'Sedán',
    engine: '3.5L V6',
    description: 'Sedán deportivo de lujo. Motor V6 potente y diseño agresivo.',
    trims: [
      {
        name: 'SV',
        auction: [3000, 4800],
        features: ['Motor 3.5L V6', 'Pantalla 8"', 'Safety Shield'],
      },
      {
        name: 'SL',
        auction: [4200, 6200],
        features: ['Cuero', 'Bose', 'Sunroof'],
      },
      {
        name: 'Platinum',
        auction: [5800, 7800],
        features: ['Cuero semi-anilina', 'Rines 19"', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'kicks-1',
    make: 'Nissan',
    model: 'Kicks',
    yearRange: '2018-2024',
    auction: [1700, 4200],
    type: 'SUV',
    engine: '1.6L 4cyl',
    description: 'Crossover subcompacto urbano. Económico y bien equipado.',
    trims: [
      {
        name: 'S',
        auction: [1700, 2500],
        features: ['Motor 1.6L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'SV',
        auction: [2300, 3200],
        features: ['Pantalla 8"', 'Blind Spot', 'Push Start'],
      },
      {
        name: 'SR',
        auction: [3000, 4200],
        features: ['Rines 17"', 'Bose', 'Techo flotante'],
      },
    ],
  },
  {
    id: 'rogue-2',
    make: 'Nissan',
    model: 'Rogue',
    yearRange: '2018-2020',
    auction: [1800, 3500],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'Cómoda, asientos Zero Gravity y buen espacio de carga.',
    trims: [
      {
        name: 'S',
        auction: [1800, 2500],
        features: ['CarPlay', 'Blind Spot', 'Frenado auto'],
      },
      {
        name: 'SV',
        auction: [2200, 3000],
        features: ['Rines 17"', 'Asientos calefactados', 'Motion Liftgate'],
      },
      {
        name: 'SL',
        auction: [2800, 3500],
        features: ['Cuero', 'Navegación', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'rogue-3',
    make: 'Nissan',
    model: 'Rogue',
    yearRange: '2021-2024',
    auction: [4200, 9500],
    type: 'SUV',
    engine: '1.5L 3cyl Turbo',
    description:
      'Interior de nivel premium, motor turbo (2022+) y mucha tecnología.',
    trims: [
      {
        name: 'S',
        auction: [4200, 5800],
        features: ['LEDs', 'Rines 17"', 'Safety Shield 360'],
      },
      {
        name: 'SV',
        auction: [5500, 7000],
        features: ['ProPILOT Assist', 'Rines 18"', 'WiFi'],
      },
      {
        name: 'SL',
        auction: [6800, 8500],
        features: ['Cuero', 'Techo panorámico', 'Rines 19"'],
      },
      {
        name: 'Platinum',
        auction: [8000, 9500],
        features: ['Tablero digital', 'Cuero Nappa', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'murano-3',
    make: 'Nissan',
    model: 'Murano',
    yearRange: '2018-2024',
    auction: [3500, 9000],
    type: 'SUV',
    engine: '3.5L V6',
    description: 'SUV mediana premium con diseño elegante y confort superior.',
    trims: [
      {
        name: 'S',
        auction: [3500, 5000],
        features: ['Motor 3.5L V6', 'Pantalla 8"', 'Safety Shield'],
      },
      {
        name: 'SV',
        auction: [4800, 6800],
        features: ['Rines 18"', 'Asientos calefactados', 'Motion Liftgate'],
      },
      {
        name: 'SL',
        auction: [6200, 7800],
        features: ['Cuero', 'Bose', 'Sunroof'],
      },
      {
        name: 'Platinum',
        auction: [7500, 9000],
        features: ['Rines 20"', 'Cuero semi-anilina', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'pathfinder-5',
    make: 'Nissan',
    model: 'Pathfinder',
    yearRange: '2022-2024',
    auction: [6200, 13000],
    type: 'SUV',
    engine: '3.5L V6',
    description: 'SUV de 3 filas rediseñada. Más robusta y con 9 velocidades.',
    trims: [
      {
        name: 'S',
        auction: [6200, 8500],
        features: ['Motor 3.5L V6', 'Pantalla 8"', 'Safety Shield 360'],
      },
      {
        name: 'SV',
        auction: [8000, 10000],
        features: ['Rines 18"', 'Asientos calefactados', 'Motion Liftgate'],
      },
      {
        name: 'SL',
        auction: [9500, 11500],
        features: ['Cuero', 'Bose', 'Techo panorámico'],
      },
      {
        name: 'Platinum',
        auction: [11000, 13000],
        features: ['Rines 20"', 'Cuero semi-anilina', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'frontier-3',
    make: 'Nissan',
    model: 'Frontier',
    yearRange: '2022-2024',
    auction: [6000, 12000],
    type: 'Pick-up',
    engine: '3.8L V6',
    description: 'Pick-up mediana totalmente rediseñada. Motor V6 potente.',
    trims: [
      {
        name: 'S',
        auction: [6000, 8000],
        features: ['Motor 3.8L V6', 'Pantalla 8"', 'Safety Shield 360'],
      },
      {
        name: 'SV',
        auction: [7500, 9500],
        features: ['Rines 17"', 'Asientos calefactados', 'Blind Spot'],
      },
      {
        name: 'PRO-4X',
        auction: [9000, 11000],
        features: ['Bilstein', 'Bloqueo diferencial', 'Skid plates'],
      },
      {
        name: 'PRO-X',
        auction: [10500, 12000],
        features: ['Rines 17" off-road', 'Fender Audio', 'Cuero'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // MAZDA
  // ═══════════════════════════════════════
  {
    id: 'mazda3-sedan',
    make: 'Mazda',
    model: '3',
    yearRange: '2019-2024',
    auction: [2800, 6800],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Diseño Kodo premiado. Interior casi de lujo en un compacto.',
    trims: [
      {
        name: 'Base',
        auction: [2800, 4000],
        features: ['Motor 2.5L', 'LEDs', 'Pantalla 8.8"'],
      },
      {
        name: 'Preferred',
        auction: [3500, 5000],
        features: ['Asiento eléctrico', 'Memoria', 'Blind Spot'],
      },
      {
        name: 'Premium',
        auction: [4800, 6800],
        features: ['Cuero', 'Head-up Display', 'Bose'],
      },
    ],
  },
  {
    id: 'mazda3-hatch',
    make: 'Mazda',
    model: '3 Hatchback',
    yearRange: '2019-2024',
    auction: [3200, 7500],
    type: 'Hatchback',
    engine: '2.5L 4cyl',
    description:
      'Diseño Kodo premiado en formato hatchback. Manejo excepcional.',
    trims: [
      {
        name: 'Base',
        auction: [3200, 4200],
        features: ['Motor 2.5L', 'LEDs', 'Pantalla 8.8"'],
      },
      {
        name: 'Preferred',
        auction: [4000, 5500],
        features: ['Asiento eléctrico', 'Memoria', 'Sunroof'],
      },
      {
        name: 'Premium',
        auction: [5200, 7500],
        features: ['Cuero', 'Head-up Display', 'Luces adaptativas'],
      },
    ],
  },
  {
    id: 'mazda6-3',
    make: 'Mazda',
    model: '6',
    yearRange: '2018-2021',
    auction: [2200, 5800],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Sedán mediano premium. Manejo europeo a precio japonés.',
    trims: [
      {
        name: 'Sport',
        auction: [2200, 3200],
        features: ['Motor 2.5L', 'Pantalla 8"', 'LEDs'],
      },
      {
        name: 'Touring',
        auction: [3000, 4200],
        features: ['Cuero', 'Sunroof', 'Bose'],
      },
      {
        name: 'Grand Touring',
        auction: [3800, 5000],
        features: ['Motor 2.5T', 'Rines 19"', 'Nappa'],
      },
      {
        name: 'Signature',
        auction: [4800, 5800],
        features: ['Cuero Nappa', 'Madera real', 'Ventilados'],
      },
    ],
  },
  {
    id: 'cx30',
    make: 'Mazda',
    model: 'CX-30',
    yearRange: '2020-2024',
    auction: [3000, 7000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'Crossover compacto con interior premium y manejo ágil.',
    trims: [
      {
        name: 'Base',
        auction: [3000, 4200],
        features: ['Motor 2.5L', 'Pantalla 8.8"', 'Blind Spot'],
      },
      {
        name: 'Preferred',
        auction: [3800, 5200],
        features: ['Asiento eléctrico', 'Rines 18"', 'Sunroof'],
      },
      {
        name: 'Premium',
        auction: [4800, 6200],
        features: ['Cuero', 'Bose', 'Head-up Display'],
      },
      {
        name: 'Turbo',
        auction: [5800, 7000],
        features: ['Motor 2.5T', 'Rines 18"', 'Nappa'],
      },
    ],
  },
  {
    id: 'cx5',
    make: 'Mazda',
    model: 'CX-5',
    yearRange: '2018-2024',
    auction: [3000, 8000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'La SUV para quienes aman manejar. Acabados de lujo.',
    trims: [
      {
        name: 'Sport',
        auction: [3000, 4200],
        features: ['LEDs', 'Blind Spot', 'Rines 17"'],
      },
      {
        name: 'Touring',
        auction: [4000, 5500],
        features: ['Cuero sintético', 'CarPlay', 'Keyless Entry'],
      },
      {
        name: 'Grand Touring',
        auction: [5200, 6800],
        features: ['Cuero', 'Sunroof', 'Bose', 'Rines 19"'],
      },
      {
        name: 'Signature',
        auction: [6200, 8000],
        features: ['Motor Turbo', 'Nappa', 'Cámaras 360'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // FORD
  // ═══════════════════════════════════════
  {
    id: 'fusion-2',
    make: 'Ford',
    model: 'Fusion',
    yearRange: '2018-2020',
    auction: [1400, 3800],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Sedán mediano elegante con opciones híbridas y AWD.',
    trims: [
      {
        name: 'S',
        auction: [1400, 2000],
        features: ['Motor 2.5L', 'Sync 3', 'Cámara reversa'],
      },
      {
        name: 'SE',
        auction: [1800, 2800],
        features: ['Motor 1.5T', 'Rines 17"', 'CarPlay'],
      },
      {
        name: 'Titanium',
        auction: [2500, 3200],
        features: ['Motor 2.0T', 'Cuero', 'Sunroof'],
      },
      {
        name: 'Sport',
        auction: [3000, 3800],
        features: ['Motor 2.7T V6', 'AWD', 'Rines 19"'],
      },
    ],
  },
  {
    id: 'ecosport-2',
    make: 'Ford',
    model: 'EcoSport',
    yearRange: '2018-2022',
    auction: [1400, 3500],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'SUV subcompacta urbana. Compacta y fácil de estacionar.',
    trims: [
      {
        name: 'S',
        auction: [1400, 2000],
        features: ['Motor 1.0T', 'Sync 3', 'Cámara reversa'],
      },
      {
        name: 'SE',
        auction: [1800, 2500],
        features: ['Motor 2.0L', 'Rines 16"', 'CarPlay'],
      },
      {
        name: 'Titanium',
        auction: [2300, 3000],
        features: ['Cuero parcial', 'Sunroof', 'B&O Audio'],
      },
      {
        name: 'SES',
        auction: [2800, 3500],
        features: ['Motor 2.0L AWD', 'Rines 17"', 'Interior deportivo'],
      },
    ],
  },
  {
    id: 'escape-3',
    make: 'Ford',
    model: 'Escape',
    yearRange: '2018-2019',
    auction: [1700, 3800],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'SUV compacta popular con opciones de motor turbo.',
    trims: [
      {
        name: 'S',
        auction: [1700, 2300],
        features: ['Motor 2.5L', 'Sync 3', 'Cámara reversa'],
      },
      {
        name: 'SE',
        auction: [2200, 2800],
        features: ['Motor 1.5T', 'Rines 17"', 'CarPlay'],
      },
      {
        name: 'SET',
        auction: [2600, 3200],
        features: ['Cuero parcial', 'Sunroof', 'Keyless'],
      },
      {
        name: 'Titanium',
        auction: [3200, 3800],
        features: ['Motor 2.0T', 'Cuero', 'B&O Audio'],
      },
    ],
  },
  {
    id: 'escape-4',
    make: 'Ford',
    model: 'Escape',
    yearRange: '2020-2024',
    auction: [3500, 8200],
    type: 'SUV',
    engine: '1.5L 3cyl Turbo',
    description: 'SUV compacta renovada con opciones híbridas y plug-in.',
    trims: [
      {
        name: 'S',
        auction: [3500, 4800],
        features: ['Motor 1.5T', 'Pantalla 8"', 'Co-Pilot360'],
      },
      {
        name: 'SE',
        auction: [4200, 5800],
        features: ['Rines 17"', 'Asientos calefactados', 'Keyless'],
      },
      {
        name: 'SEL',
        auction: [5500, 7000],
        features: ['Cuero parcial', 'Pantalla 13.2"', 'B&O Audio'],
      },
      {
        name: 'Titanium',
        auction: [6800, 8200],
        features: ['Cuero', 'Techo panorámico', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'edge-2',
    make: 'Ford',
    model: 'Edge',
    yearRange: '2018-2024',
    auction: [3000, 9000],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV mediana con buen espacio y opciones deportivas.',
    trims: [
      {
        name: 'SE',
        auction: [3000, 4800],
        features: ['Motor 2.0T', 'Sync 3', 'Co-Pilot360'],
      },
      {
        name: 'SEL',
        auction: [4200, 5800],
        features: ['Cuero parcial', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: 'Titanium',
        auction: [5500, 7500],
        features: ['Cuero', 'Sunroof', 'B&O Audio'],
      },
      {
        name: 'ST',
        auction: [7000, 9000],
        features: ['Motor 2.7T V6', 'Rines 21"', 'Suspensión deportiva'],
      },
    ],
  },
  {
    id: 'broncosport-1',
    make: 'Ford',
    model: 'Bronco Sport',
    yearRange: '2021-2024',
    auction: [5500, 11000],
    type: 'SUV',
    engine: '1.5L 3cyl Turbo',
    description: 'SUV compacta aventurera con capacidad off-road real.',
    trims: [
      {
        name: 'Base',
        auction: [5500, 6800],
        features: ['Motor 1.5T', 'Pantalla 8"', 'Co-Pilot360'],
      },
      {
        name: 'Big Bend',
        auction: [6800, 8000],
        features: ['Rines 17"', 'Asientos calefactados', 'Keyless'],
      },
      {
        name: 'Outer Banks',
        auction: [8000, 9500],
        features: ['Cuero', 'Rines 18"', 'B&O Audio'],
      },
      {
        name: 'Badlands',
        auction: [9200, 11000],
        features: ['Motor 2.0T', 'Trail Control', 'Diferencial trasero'],
      },
    ],
  },
  {
    id: 'explorer-6',
    make: 'Ford',
    model: 'Explorer',
    yearRange: '2020-2024',
    auction: [6500, 15000],
    type: 'SUV',
    engine: '2.3L 4cyl Turbo',
    description: 'SUV grande con 3 filas, tracción trasera y motor potente.',
    trims: [
      {
        name: 'Base',
        auction: [6500, 9000],
        features: ['Motor 2.3T', 'Pantalla 8"', 'Co-Pilot360'],
      },
      {
        name: 'XLT',
        auction: [8500, 10500],
        features: ['Rines 20"', 'Asientos calefactados', 'Keyless'],
      },
      {
        name: 'Limited',
        auction: [10000, 12500],
        features: ['Cuero', 'B&O Audio', 'Techo panorámico'],
      },
      {
        name: 'ST',
        auction: [12500, 15000],
        features: ['Motor 3.0T V6', 'Rines 21"', 'Suspensión adaptativa'],
      },
    ],
  },
  {
    id: 'ranger-3',
    make: 'Ford',
    model: 'Ranger',
    yearRange: '2019-2024',
    auction: [4800, 12000],
    type: 'Pick-up',
    engine: '2.3L 4cyl Turbo',
    description: 'Pick-up mediana con motor EcoBoost y capacidad off-road.',
    trims: [
      {
        name: 'XL',
        auction: [4800, 6800],
        features: ['Motor 2.3T', 'Pantalla 8"', 'Co-Pilot360'],
      },
      {
        name: 'XLT',
        auction: [6200, 8500],
        features: ['Rines 17"', 'CarPlay', 'Asientos calefactados'],
      },
      {
        name: 'Lariat',
        auction: [8000, 10500],
        features: ['Cuero', 'B&O Audio', 'Navegación'],
      },
      {
        name: 'Tremor',
        auction: [9500, 12000],
        features: ['Fox shocks', 'Rines 17" off-road', 'Trail Control'],
      },
    ],
  },
  {
    id: 'f150-13',
    make: 'Ford',
    model: 'F-150',
    yearRange: '2018-2020',
    auction: [5500, 12000],
    type: 'Pick-up',
    engine: '3.5L V6 Turbo',
    description: 'Cuerpo de aluminio, motores EcoBoost potentes.',
    trims: [
      {
        name: 'XL',
        auction: [5500, 7500],
        features: ['Básico', 'V6 o V8', 'Cámara reversa'],
      },
      {
        name: 'XLT',
        auction: [7000, 9500],
        features: ['Sync 3', 'Cromo', 'Asientos tela premium'],
      },
      {
        name: 'Lariat',
        auction: [9500, 12000],
        features: ['Cuero', 'Ventilados', 'Pantalla 8"'],
      },
    ],
  },
  {
    id: 'f150-14',
    make: 'Ford',
    model: 'F-150',
    yearRange: '2021-2024',
    auction: [11500, 22000],
    type: 'Pick-up',
    engine: '3.5L V6 Turbo',
    description: 'La más tecnológica. Pantallas enormes y opción híbrida.',
    trims: [
      {
        name: 'XL',
        auction: [11500, 14000],
        features: ['Pantalla 8"', 'Safety Tech', 'CarPlay'],
      },
      {
        name: 'XLT',
        auction: [14000, 18000],
        features: ['Pantalla 12" (opc)', 'Pro Power', '360 Camera'],
      },
      {
        name: 'Lariat',
        auction: [17500, 22000],
        features: ['Pantalla 12" std', 'Cuero', 'Digital Cluster'],
      },
    ],
  },
  {
    id: 'mustang-6',
    make: 'Ford',
    model: 'Mustang',
    yearRange: '2018-2024',
    auction: [4500, 15000],
    type: 'Deportivo',
    engine: '2.3L 4cyl Turbo',
    description: 'El muscle car americano por excelencia. Potencia y estilo.',
    trims: [
      {
        name: 'EcoBoost',
        auction: [4500, 6500],
        features: ['Motor 2.3T 310hp', 'Pantalla 8"', 'Rines 17"'],
      },
      {
        name: 'EcoBoost Premium',
        auction: [6000, 9000],
        features: ['Cuero', 'B&O Audio', 'Digital Cluster'],
      },
      {
        name: 'GT',
        auction: [8000, 11500],
        features: ['Motor 5.0L V8 460hp', 'Rines 18"', 'Modo Track'],
      },
      {
        name: 'GT Premium',
        auction: [10500, 15000],
        features: ['Cuero', 'Recaro (opc)', 'MagneRide'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // CHEVROLET
  // ═══════════════════════════════════════
  {
    id: 'spark-4',
    make: 'Chevrolet',
    model: 'Spark',
    yearRange: '2018-2022',
    auction: [300, 1000],
    type: 'Hatchback',
    engine: '1.4L 4cyl',
    description: 'El auto más económico. Ideal para ciudad y primer auto.',
    trims: [
      {
        name: 'LS',
        auction: [300, 500],
        features: ['Motor 1.4L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LT',
        auction: [450, 750],
        features: ['Rines 15"', 'Cámara reversa', 'Keyless'],
      },
      {
        name: 'ACTIV',
        auction: [700, 1000],
        features: ['Estilo crossover', 'Rieles techo', 'Interior especial'],
      },
    ],
  },
  {
    id: 'cruze-2',
    make: 'Chevrolet',
    model: 'Cruze',
    yearRange: '2018-2019',
    auction: [800, 2000],
    type: 'Sedán',
    engine: '1.4L 4cyl Turbo',
    description: 'Compacto económico con motor turbo eficiente.',
    trims: [
      {
        name: 'L',
        auction: [800, 1100],
        features: ['Motor 1.4T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [1000, 1300],
        features: ['Rines 16"', 'Keyless', 'Cámara reversa'],
      },
      {
        name: 'LT',
        auction: [1200, 1600],
        features: ['Rines 17"', 'Asiento eléctrico', 'Climatizador'],
      },
      {
        name: 'Premier',
        auction: [1500, 2000],
        features: ['Cuero', 'Asientos calefactados', 'Bose'],
      },
    ],
  },
  {
    id: 'malibu-9',
    make: 'Chevrolet',
    model: 'Malibu',
    yearRange: '2018-2024',
    auction: [1500, 5500],
    type: 'Sedán',
    engine: '1.5L 4cyl Turbo',
    description: 'Sedán mediano espacioso con buen equipamiento.',
    trims: [
      {
        name: 'L',
        auction: [1500, 2500],
        features: ['Motor 1.5T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [2200, 3200],
        features: ['Rines 16"', 'Keyless', 'Teen Driver'],
      },
      {
        name: 'RS',
        auction: [3000, 4200],
        features: ['Rines negros 18"', 'Spoiler', 'Interior deportivo'],
      },
      {
        name: 'Premier',
        auction: [4200, 5500],
        features: ['Cuero', 'Bose', 'Asientos ventilados'],
      },
    ],
  },
  {
    id: 'camaro-6',
    make: 'Chevrolet',
    model: 'Camaro',
    yearRange: '2018-2024',
    auction: [4500, 15000],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description: 'Muscle car americano. Potencia bruta y manejo preciso.',
    trims: [
      {
        name: '1LT',
        auction: [4500, 6500],
        features: ['Motor 2.0T 275hp', 'Pantalla 8"', 'Rines 18"'],
      },
      {
        name: '1SS',
        auction: [7000, 9500],
        features: ['Motor 6.2L V8 455hp', 'Brembo', 'Modo Track'],
      },
      {
        name: '2SS',
        auction: [9000, 11500],
        features: ['Motor 6.2L V8', 'Head-up Display', 'Recaro opc'],
      },
      {
        name: 'ZL1',
        auction: [12000, 15000],
        features: ['Motor 6.2L SC 650hp', 'MagneRide', 'Alerón carbono'],
      },
    ],
  },
  {
    id: 'trailblazer-3',
    make: 'Chevrolet',
    model: 'Trailblazer',
    yearRange: '2021-2024',
    auction: [3000, 6200],
    type: 'SUV',
    engine: '1.3L 3cyl Turbo',
    description: 'SUV subcompacta con diseño moderno y buen equipamiento.',
    trims: [
      {
        name: 'L',
        auction: [3000, 3800],
        features: ['Motor 1.2T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [3600, 4500],
        features: ['Rines 17"', 'Cámara reversa', 'Keyless'],
      },
      {
        name: 'LT',
        auction: [4200, 5200],
        features: ['Rines 18"', 'Asientos calefactados', 'Blind Spot'],
      },
      {
        name: 'RS',
        auction: [5000, 6200],
        features: ['Rines negros 18"', 'Interior deportivo', 'Bose'],
      },
    ],
  },
  {
    id: 'trax-2',
    make: 'Chevrolet',
    model: 'Trax',
    yearRange: '2024',
    auction: [4200, 6800],
    type: 'SUV',
    engine: '1.2L 3cyl Turbo',
    description: 'Totalmente rediseñada. Mucho más grande y moderna.',
    trims: [
      {
        name: 'LS',
        auction: [4200, 5000],
        features: ['Motor 1.2T', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'LT',
        auction: [4800, 5600],
        features: ['Rines 18"', 'Asiento eléctrico', 'Blind Spot'],
      },
      {
        name: 'RS',
        auction: [5500, 6200],
        features: ['Rines negros 19"', 'Interior deportivo', 'Bose'],
      },
      {
        name: 'Activ',
        auction: [5800, 6800],
        features: ['Estilo off-road', 'Rieles techo', 'Protectores'],
      },
    ],
  },
  {
    id: 'equinox-3',
    make: 'Chevrolet',
    model: 'Equinox',
    yearRange: '2018-2024',
    auction: [2200, 7000],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'SUV compacta versátil con buen espacio y tecnología.',
    trims: [
      {
        name: 'L',
        auction: [2200, 3500],
        features: ['Motor 1.5T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [3000, 4200],
        features: ['Rines 17"', 'Safety Tech', 'Keyless'],
      },
      {
        name: 'LT',
        auction: [4000, 5500],
        features: ['Rines 18"', 'Power Liftgate', 'Asientos calefactados'],
      },
      {
        name: 'Premier',
        auction: [5500, 7000],
        features: ['Cuero', 'Bose', 'Techo panorámico'],
      },
    ],
  },
  {
    id: 'blazer-3',
    make: 'Chevrolet',
    model: 'Blazer',
    yearRange: '2019-2024',
    auction: [4800, 11500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV mediana con diseño Camaro-inspired y motor potente.',
    trims: [
      {
        name: 'L',
        auction: [4800, 6200],
        features: ['Motor 2.0T', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'LT',
        auction: [5800, 7800],
        features: ['Rines 18"', 'Asientos calefactados', 'Keyless'],
      },
      {
        name: 'RS',
        auction: [7500, 9500],
        features: ['Rines negros 21"', 'Interior deportivo', 'Bose'],
      },
      {
        name: 'Premier',
        auction: [9200, 11500],
        features: ['Motor 3.6L V6', 'Cuero', 'Techo panorámico'],
      },
    ],
  },
  {
    id: 'traverse-2',
    make: 'Chevrolet',
    model: 'Traverse',
    yearRange: '2018-2024',
    auction: [4200, 11000],
    type: 'SUV',
    engine: '3.6L V6',
    description: 'SUV grande de 3 filas con mucho espacio para familias.',
    trims: [
      {
        name: 'L',
        auction: [4200, 5800],
        features: ['Motor 3.6L V6', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [5500, 7000],
        features: ['Rines 18"', 'Cámara reversa', 'Keyless'],
      },
      {
        name: 'LT',
        auction: [6800, 8800],
        features: ['Cuero parcial', 'Bose', 'Power Liftgate'],
      },
      {
        name: 'Premier/High Country',
        auction: [8500, 11000],
        features: ['Cuero', 'Techo panorámico', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'colorado-3',
    make: 'Chevrolet',
    model: 'Colorado',
    yearRange: '2018-2024',
    auction: [4200, 12000],
    type: 'Pick-up',
    engine: '3.6L V6',
    description: 'Pick-up mediana versátil con opciones diesel y off-road.',
    trims: [
      {
        name: 'W T',
        auction: [4200, 5800],
        features: ['Motor 2.5L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LT',
        auction: [5500, 8000],
        features: ['Motor 3.6L V6', 'Rines 17"', 'Asientos calefactados'],
      },
      {
        name: 'Z71',
        auction: [7500, 10000],
        features: ['Off-road pkg', 'Rines 17"', 'Skid plates'],
      },
      {
        name: 'ZR2',
        auction: [9500, 12000],
        features: ['Multimatic DSSV', 'Bloqueos', 'Rines 17" off-road'],
      },
    ],
  },
  {
    id: 'silverado-4',
    make: 'Chevrolet',
    model: 'Silverado 1500',
    yearRange: '2019-2024',
    auction: [7500, 18000],
    type: 'Pick-up',
    engine: '2.7L 4cyl Turbo',
    description: 'Pick-up full-size con opciones de motor V6, V8 y diesel.',
    trims: [
      {
        name: 'WT',
        auction: [7500, 10000],
        features: ['Motor 2.7T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'Custom',
        auction: [9500, 12000],
        features: ['Rines 20"', 'LEDs', 'Carrocería color'],
      },
      {
        name: 'LT',
        auction: [11500, 15000],
        features: ['Rines cromados', 'Asientos calefactados', 'Trailering'],
      },
      {
        name: 'LTZ',
        auction: [14500, 18000],
        features: ['Cuero', 'Bose', 'Techo panorámico'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // VOLKSWAGEN
  // ═══════════════════════════════════════
  {
    id: 'jetta-7',
    make: 'Volkswagen',
    model: 'Jetta',
    yearRange: '2019-2024',
    auction: [2000, 5500],
    type: 'Sedán',
    engine: '1.5L 4cyl Turbo',
    description: 'Ingeniería alemana accesible. Sólido, refinado y eficiente.',
    trims: [
      {
        name: 'S',
        auction: [2000, 3000],
        features: ['Motor 1.5T', 'Pantalla 6.5"', 'CarPlay'],
      },
      {
        name: 'SE',
        auction: [2800, 3800],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [3500, 4800],
        features: ['Cuero', 'Digital Cockpit', 'Beats Audio'],
      },
      {
        name: 'GLI',
        auction: [4200, 5500],
        features: ['Motor 2.0T', 'Suspensión deportiva', 'Rines 18"'],
      },
    ],
  },
  {
    id: 'passat-8',
    make: 'Volkswagen',
    model: 'Passat',
    yearRange: '2018-2022',
    auction: [1500, 4800],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description: 'Sedán mediano espacioso con confort y calidad alemana.',
    trims: [
      {
        name: 'S',
        auction: [1500, 2200],
        features: ['Motor 2.0T', 'Pantalla 6.3"', 'CarPlay'],
      },
      {
        name: 'SE',
        auction: [2200, 3200],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [3000, 3800],
        features: ['Cuero', 'Digital Cockpit', 'Fender Audio'],
      },
      {
        name: 'SEL Premium',
        auction: [3800, 4800],
        features: ['Ventilados', 'Cámaras 360', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'taos-1',
    make: 'Volkswagen',
    model: 'Taos',
    yearRange: '2022-2024',
    auction: [4500, 9700],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'SUV subcompacta nueva. Espaciosa para su segmento.',
    trims: [
      {
        name: 'S',
        auction: [4500, 6800],
        features: ['Motor 1.5T', 'Pantalla 6.5"', 'Blind Spot'],
      },
      {
        name: 'SE',
        auction: [5500, 8100],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [6500, 9700],
        features: ['Cuero', 'Digital Cockpit', 'Beats Audio'],
      },
    ],
  },
  {
    id: 'tiguan-2',
    make: 'Volkswagen',
    model: 'Tiguan',
    yearRange: '2018-2024',
    auction: [2800, 8000],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV compacta alemana con opción de 3 filas y manejo europeo.',
    trims: [
      {
        name: 'S',
        auction: [2800, 4200],
        features: ['Motor 2.0T', 'Pantalla 6.5"', 'Blind Spot'],
      },
      {
        name: 'SE',
        auction: [3800, 5500],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [5000, 6800],
        features: ['Cuero', 'Digital Cockpit', 'Fender Audio'],
      },
      {
        name: 'SEL Premium',
        auction: [6200, 8000],
        features: ['Rines 20"', 'Asientos ventilados', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'atlas-1',
    make: 'Volkswagen',
    model: 'Atlas',
    yearRange: '2018-2024',
    auction: [4200, 11000],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV grande con 3 filas reales. Mucho espacio para familias.',
    trims: [
      {
        name: 'S',
        auction: [4200, 5800],
        features: ['Motor 2.0T', 'Pantalla 6.5"', 'Blind Spot'],
      },
      {
        name: 'SE',
        auction: [5800, 7800],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [7500, 9500],
        features: ['Cuero', 'Digital Cockpit', 'Fender Audio'],
      },
      {
        name: 'SEL Premium',
        auction: [9200, 11000],
        features: ['Motor V6', 'Asientos ventilados', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'atlascross-1',
    make: 'Volkswagen',
    model: 'Atlas Cross Sport',
    yearRange: '2020-2024',
    auction: [4800, 11000],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'Atlas en formato coupé. Más deportivo, 2 filas.',
    trims: [
      {
        name: 'S',
        auction: [4800, 6200],
        features: ['Motor 2.0T', 'Pantalla 6.5"', 'Blind Spot'],
      },
      {
        name: 'SE',
        auction: [5800, 7800],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [7500, 9500],
        features: ['Cuero', 'Digital Cockpit', 'Fender Audio'],
      },
      {
        name: 'SEL Premium',
        auction: [9200, 11000],
        features: ['Motor V6', 'Ventilados', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'golf-8',
    make: 'Volkswagen',
    model: 'Golf',
    yearRange: '2018-2024',
    auction: [2200, 9500],
    type: 'Hatchback',
    engine: '1.4L 4cyl Turbo',
    description:
      'El hatchback por excelencia. Versátil, divertido y bien construido.',
    trims: [
      {
        name: 'TSI',
        auction: [2200, 3800],
        features: ['Motor 1.4T', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'SE',
        auction: [3500, 5000],
        features: ['Sunroof', 'Cuero', 'Digital Cockpit'],
      },
      {
        name: 'GTI S',
        auction: [4800, 7000],
        features: ['Motor 2.0T', 'Rines 18"', 'Diferencial LSD'],
      },
      {
        name: 'GTI Autobahn',
        auction: [6800, 9500],
        features: ['Cuero Clark Plaid', 'Fender Audio', 'DCC'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // BMW
  // ═══════════════════════════════════════
  {
    id: 'bmw-2-f22',
    make: 'BMW',
    model: '2 Series',
    yearRange: '2018-2024',
    auction: [4500, 11500],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description:
      'Coupé compacto deportivo. Manejo puro BMW en formato pequeño.',
    trims: [
      {
        name: '230i',
        auction: [4500, 7000],
        features: ['Motor 2.0T', 'iDrive', 'Asientos deportivos'],
      },
      {
        name: '230i xDrive',
        auction: [5800, 8500],
        features: ['AWD', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: 'M240i',
        auction: [8500, 11500],
        features: ['Motor 3.0T', 'M Sport', 'Diferencial M'],
      },
    ],
  },
  {
    id: 'bmw-3-g20',
    make: 'BMW',
    model: '3 Series',
    yearRange: '2019-2024',
    auction: [7000, 17000],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description:
      'El sedán deportivo de referencia. Manejo puro, tecnología premium.',
    trims: [
      {
        name: '330i',
        auction: [7000, 10000],
        features: ['Motor 2.0T 255hp', 'iDrive 7', 'Asientos deportivos'],
      },
      {
        name: '330i xDrive',
        auction: [8500, 12000],
        features: ['AWD', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: 'M340i',
        auction: [11500, 14500],
        features: ['Motor 3.0T 382hp', 'M Sport', 'Harman Kardon'],
      },
      {
        name: 'M340i xDrive',
        auction: [13500, 17000],
        features: ['AWD', 'Frenos M', 'Escape M Sport'],
      },
    ],
  },
  {
    id: 'bmw-4-g22',
    make: 'BMW',
    model: '4 Series',
    yearRange: '2021-2024',
    auction: [9500, 18000],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description: 'Coupé/Gran Coupé elegante con diseño audaz y motor potente.',
    trims: [
      {
        name: '430i',
        auction: [9500, 12000],
        features: ['Motor 2.0T', 'iDrive 7', 'Cuero SensaTec'],
      },
      {
        name: '430i xDrive',
        auction: [11000, 14500],
        features: ['AWD', 'Rines 19"', 'Asientos calefactados'],
      },
      {
        name: 'M440i xDrive',
        auction: [14500, 18000],
        features: ['Motor 3.0T 382hp', 'M Sport', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'bmw-5-g30',
    make: 'BMW',
    model: '5 Series',
    yearRange: '2018-2024',
    auction: [6500, 18000],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description:
      'Sedán ejecutivo de lujo. Confort, tecnología y manejo deportivo.',
    trims: [
      {
        name: '530i',
        auction: [6500, 10000],
        features: ['Motor 2.0T', 'iDrive', 'Cuero'],
      },
      {
        name: '530i xDrive',
        auction: [8500, 12000],
        features: ['AWD', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: '540i',
        auction: [10500, 14500],
        features: ['Motor 3.0T', 'M Sport', 'Harman Kardon'],
      },
      {
        name: 'M550i',
        auction: [14000, 18000],
        features: ['Motor 4.4T V8', 'M Sport Pro', 'Frenos M'],
      },
    ],
  },
  {
    id: 'bmw-x1-f48',
    make: 'BMW',
    model: 'X1',
    yearRange: '2018-2022',
    auction: [4500, 9500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV compacta premium. Entrada al mundo BMW con estilo.',
    trims: [
      {
        name: 'sDrive28i',
        auction: [4500, 6000],
        features: ['Motor 2.0T', 'Pantalla 6.5"', 'Cuero SensaTec'],
      },
      {
        name: 'xDrive28i',
        auction: [5500, 7800],
        features: ['AWD', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: 'M35i',
        auction: [7500, 9500],
        features: ['Motor 2.0T 302hp', 'M Sport', 'Escape M'],
      },
    ],
  },
  {
    id: 'bmw-x1-u11',
    make: 'BMW',
    model: 'X1',
    yearRange: '2023-2024',
    auction: [9500, 15500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description:
      'Nueva generación totalmente rediseñada. Más grande y tecnológica.',
    trims: [
      {
        name: 'xDrive28i',
        auction: [9500, 12000],
        features: ['Motor 2.0T', 'iDrive 8', 'Curved Display'],
      },
      {
        name: 'M35i xDrive',
        auction: [12500, 15500],
        features: ['Motor 2.0T 312hp', 'M Sport', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'bmw-x3-g01',
    make: 'BMW',
    model: 'X3',
    yearRange: '2018-2024',
    auction: [6500, 15500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description:
      'SUV premium deportiva. Equilibrio perfecto entre lujo y manejo.',
    trims: [
      {
        name: 'sDrive30i',
        auction: [6500, 9000],
        features: ['Motor 2.0T', 'iDrive', 'Cuero SensaTec'],
      },
      {
        name: 'xDrive30i',
        auction: [8000, 11500],
        features: ['AWD', 'Rines 19"', 'Asientos calefactados'],
      },
      {
        name: 'M40i',
        auction: [11500, 15500],
        features: ['Motor 3.0T 382hp', 'M Sport', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'bmw-x5-g05',
    make: 'BMW',
    model: 'X5',
    yearRange: '2019-2024',
    auction: [12500, 23000],
    type: 'SUV',
    engine: '3.0L 6cyl Turbo',
    description: 'SUV grande de lujo. Potente, tecnológica y muy capaz.',
    trims: [
      {
        name: 'sDrive40i',
        auction: [12500, 15000],
        features: ['Motor 3.0T', 'iDrive 7', 'Cuero'],
      },
      {
        name: 'xDrive40i',
        auction: [15000, 19000],
        features: ['AWD', 'Rines 20"', 'Panoramic Sky Lounge'],
      },
      {
        name: 'M50i',
        auction: [19000, 23000],
        features: ['Motor 4.4T V8', 'M Sport', 'Bowers & Wilkins'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // AUDI
  // ═══════════════════════════════════════
  {
    id: 'audi-a3-8v',
    make: 'Audi',
    model: 'A3',
    yearRange: '2018-2024',
    auction: [4500, 11000],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description: 'Sedán compacto premium. Lujo alemán en formato accesible.',
    trims: [
      {
        name: 'Premium',
        auction: [4500, 6500],
        features: ['Motor 2.0T', 'Virtual Cockpit', 'Cuero'],
      },
      {
        name: 'Premium Plus',
        auction: [6000, 8500],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof'],
      },
      {
        name: 'Prestige',
        auction: [8000, 11000],
        features: ['Head-up Display', 'Navegación', 'Matrix LED'],
      },
    ],
  },
  {
    id: 'audi-a4-b9',
    make: 'Audi',
    model: 'A4',
    yearRange: '2018-2024',
    auction: [5500, 14000],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description: 'Sedán de lujo con tecnología Virtual Cockpit y quattro AWD.',
    trims: [
      {
        name: 'Premium',
        auction: [5500, 8000],
        features: ['Motor 2.0T', 'Virtual Cockpit', 'Cuero'],
      },
      {
        name: 'Premium Plus',
        auction: [7800, 11000],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof'],
      },
      {
        name: 'Prestige',
        auction: [10500, 14000],
        features: ['Head-up Display', 'Cámaras 360', 'Navegación'],
      },
    ],
  },
  {
    id: 'audi-a5-f5',
    make: 'Audi',
    model: 'A5',
    yearRange: '2018-2024',
    auction: [7000, 15500],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description:
      'Coupé/Sportback elegante con diseño atemporal y motor potente.',
    trims: [
      {
        name: 'Premium',
        auction: [7000, 10000],
        features: ['Motor 2.0T', 'Virtual Cockpit', 'Cuero'],
      },
      {
        name: 'Premium Plus',
        auction: [9500, 12500],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof'],
      },
      {
        name: 'Prestige',
        auction: [12000, 15500],
        features: ['Head-up Display', 'Cámaras 360', 'Matrix LED'],
      },
    ],
  },
  {
    id: 'audi-a6-c8',
    make: 'Audi',
    model: 'A6',
    yearRange: '2019-2024',
    auction: [9500, 18000],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description:
      'Sedán ejecutivo de lujo con tecnología dual-screen y quattro.',
    trims: [
      {
        name: 'Premium',
        auction: [9500, 12000],
        features: ['Motor 2.0T', 'Dual Screen', 'Cuero'],
      },
      {
        name: 'Premium Plus',
        auction: [11500, 14500],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof'],
      },
      {
        name: 'Prestige',
        auction: [14000, 18000],
        features: ['Head-up Display', 'Cámaras 360', 'Navegación'],
      },
    ],
  },
  {
    id: 'audi-q3-f3',
    make: 'Audi',
    model: 'Q3',
    yearRange: '2019-2024',
    auction: [5500, 11000],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description:
      'SUV compacta premium. Entrada al mundo Audi con estilo urbano.',
    trims: [
      {
        name: 'Premium',
        auction: [5500, 7200],
        features: ['Motor 2.0T', 'quattro AWD', 'Pantalla 10.1"'],
      },
      {
        name: 'Premium Plus',
        auction: [7000, 9200],
        features: ['Audi Side Assist', 'Sunroof panorámico', 'Cuero'],
      },
      {
        name: 'Prestige',
        auction: [9000, 11000],
        features: ['Virtual Cockpit Plus', 'Bang & Olufsen', 'Navegación'],
      },
    ],
  },
  {
    id: 'audi-q5-fy',
    make: 'Audi',
    model: 'Q5',
    yearRange: '2018-2024',
    auction: [7000, 15500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV premium con quattro AWD de serie. Refinada y capaz.',
    trims: [
      {
        name: 'Premium',
        auction: [7000, 10000],
        features: ['Motor 2.0T', 'quattro AWD', 'Virtual Cockpit'],
      },
      {
        name: 'Premium Plus',
        auction: [9500, 12500],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof panorámico'],
      },
      {
        name: 'Prestige',
        auction: [12000, 15500],
        features: ['Head-up Display', 'Cámaras 360', 'Navegación'],
      },
    ],
  },
  {
    id: 'audi-q7-4m',
    make: 'Audi',
    model: 'Q7',
    yearRange: '2018-2024',
    auction: [8000, 18000],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV grande de lujo con 3 filas y quattro AWD.',
    trims: [
      {
        name: 'Premium',
        auction: [8000, 11000],
        features: ['Motor 2.0T', 'quattro AWD', 'Virtual Cockpit'],
      },
      {
        name: 'Premium Plus',
        auction: [10500, 14000],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof panorámico'],
      },
      {
        name: 'Prestige',
        auction: [13500, 18000],
        features: ['Head-up Display', 'Cámaras 360', 'Asientos ventilados'],
      },
    ],
  },
  {
    id: 'audi-q8-4m',
    make: 'Audi',
    model: 'Q8',
    yearRange: '2019-2024',
    auction: [13000, 22000],
    type: 'SUV',
    engine: '3.0L V6 Turbo',
    description: 'SUV coupé flagship. Diseño imponente y tecnología de punta.',
    trims: [
      {
        name: 'Premium',
        auction: [13000, 16000],
        features: ['Motor 3.0T V6', 'quattro AWD', 'Dual Screen'],
      },
      {
        name: 'Premium Plus',
        auction: [15500, 19000],
        features: ['Bang & Olufsen', 'Sunroof panorámico', 'Matrix LED'],
      },
      {
        name: 'Prestige',
        auction: [18500, 22000],
        features: ['Head-up Display', 'Cámaras 360', 'Asientos masaje'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // DEPORTIVOS ADICIONALES
  // ═══════════════════════════════════════
  {
    id: 'corvette-c7',
    make: 'Chevrolet',
    model: 'Corvette',
    yearRange: '2018-2019',
    auction: [15000, 27000],
    type: 'Deportivo',
    engine: '6.2L V8',
    description:
      'Deportivo americano icónico. Motor V8 frontal, rendimiento puro.',
    trims: [
      {
        name: '1LT',
        auction: [15000, 18500],
        features: ['Motor 6.2L V8 455hp', 'Rines 18/19"', 'Modo Track'],
      },
      {
        name: '2LT',
        auction: [17500, 21000],
        features: ['Cuero', 'Head-up Display', 'Bose'],
      },
      {
        name: '3LT',
        auction: [20000, 24000],
        features: ['Cuero Nappa', 'Navegación', 'Cámaras'],
      },
      {
        name: 'Z06',
        auction: [23500, 27000],
        features: ['Motor 6.2L SC 650hp', 'Aero pkg', 'MagneRide'],
      },
    ],
  },
  {
    id: 'corvette-c8',
    make: 'Chevrolet',
    model: 'Corvette',
    yearRange: '2020-2024',
    auction: [25000, 42000],
    type: 'Deportivo',
    engine: '6.2L V8',
    description: 'Motor central revolucionario. Rendimiento de superdeportivo.',
    trims: [
      {
        name: '1LT',
        auction: [25000, 30000],
        features: ['Motor 6.2L V8 490hp', 'Motor central', 'Modo Track'],
      },
      {
        name: '2LT',
        auction: [29000, 34000],
        features: ['Cuero GT2', 'Head-up Display', 'Bose'],
      },
      {
        name: '3LT',
        auction: [33000, 38000],
        features: ['Cuero Nappa', 'Suede microfiber', 'Cámaras'],
      },
      {
        name: 'Z06',
        auction: [38000, 42000],
        features: ['Motor 5.5L V8 670hp', 'Aero pkg', 'Carbono'],
      },
    ],
  },
  {
    id: 'challenger-3',
    make: 'Dodge',
    model: 'Challenger',
    yearRange: '2018-2023',
    auction: [6000, 22000],
    type: 'Deportivo',
    engine: '3.6L V6',
    description: 'Muscle car retro con opciones de motor V6 hasta Hellcat.',
    trims: [
      {
        name: 'SXT',
        auction: [6000, 8500],
        features: ['Motor 3.6L V6 303hp', 'Pantalla 7"', 'Rines 18"'],
      },
      {
        name: 'R/T',
        auction: [9000, 12000],
        features: ['Motor 5.7L HEMI V8 375hp', 'Escape activo', 'Rines 20"'],
      },
      {
        name: 'R/T Scat Pack',
        auction: [12000, 16000],
        features: ['Motor 6.4L V8 485hp', 'Brembo', 'Launch Control'],
      },
      {
        name: 'SRT Hellcat',
        auction: [18000, 22000],
        features: ['Motor 6.2L SC 717hp', 'Bilstein', 'SRT Drive Modes'],
      },
    ],
  },
  {
    id: 'charger-7',
    make: 'Dodge',
    model: 'Charger',
    yearRange: '2018-2023',
    auction: [5500, 20000],
    type: 'Deportivo',
    engine: '3.6L V6',
    description: 'Sedán muscle car de 4 puertas. Potencia y practicidad.',
    trims: [
      {
        name: 'SXT',
        auction: [5500, 8000],
        features: ['Motor 3.6L V6 300hp', 'Pantalla 8.4"', 'Rines 17"'],
      },
      {
        name: 'R/T',
        auction: [8500, 11500],
        features: ['Motor 5.7L HEMI V8 370hp', 'Escape activo', 'Rines 20"'],
      },
      {
        name: 'Scat Pack',
        auction: [11500, 15000],
        features: ['Motor 6.4L V8 485hp', 'Brembo', 'Launch Control'],
      },
      {
        name: 'SRT Hellcat',
        auction: [17000, 20000],
        features: ['Motor 6.2L SC 717hp', 'Bilstein', 'SRT Modes'],
      },
    ],
  },
  {
    id: 'brz-2',
    make: 'Subaru',
    model: 'BRZ',
    yearRange: '2022-2024',
    auction: [8000, 12500],
    type: 'Deportivo',
    engine: '2.4L 4cyl',
    description: 'Coupé deportivo ligero con motor boxer. Manejo puro.',
    trims: [
      {
        name: 'Premium',
        auction: [8000, 10000],
        features: ['Motor 2.4L 228hp', 'Pantalla 8"', 'Rines 17"'],
      },
      {
        name: 'Limited',
        auction: [10000, 12500],
        features: ['Cuero/Alcantara', 'Rines 18"', 'Blind Spot'],
      },
    ],
  },
  {
    id: 'wrx-5',
    make: 'Subaru',
    model: 'WRX',
    yearRange: '2022-2024',
    auction: [8000, 13500],
    type: 'Deportivo',
    engine: '2.4L 4cyl Turbo',
    description: 'Sedán deportivo AWD con motor turbo. Rally en la calle.',
    trims: [
      {
        name: 'Base',
        auction: [8000, 10000],
        features: ['Motor 2.4T 271hp', 'AWD', 'Pantalla 11.6"'],
      },
      {
        name: 'Premium',
        auction: [9500, 11500],
        features: ['Sunroof', 'Recaro opc', 'Rines 18"'],
      },
      {
        name: 'GT',
        auction: [11000, 13500],
        features: ['Cuero', 'Harman Kardon', 'Suspensión adaptativa'],
      },
    ],
  },
  {
    id: '370z',
    make: 'Nissan',
    model: '370Z',
    yearRange: '2018-2020',
    auction: [5500, 11500],
    type: 'Deportivo',
    engine: '3.7L V6',
    description:
      'Coupé deportivo japonés clásico. Motor V6 aspirado y manejo puro.',
    trims: [
      {
        name: 'Base',
        auction: [5500, 7200],
        features: ['Motor 3.7L V6 332hp', 'Rines 18"', 'Pantalla 7"'],
      },
      {
        name: 'Sport',
        auction: [7000, 9000],
        features: ['Syncro Rev Match', 'Rines 19"', 'Viscous LSD'],
      },
      {
        name: 'Sport Touring',
        auction: [8500, 11500],
        features: ['Cuero', 'Navegación', 'Bose'],
      },
    ],
  },
  {
    id: 'nissan-z',
    make: 'Nissan',
    model: 'Z',
    yearRange: '2023-2024',
    auction: [13500, 19000],
    type: 'Deportivo',
    engine: '3.0L V6 Twin Turbo',
    description: 'El nuevo Z. Motor twin-turbo V6 con diseño retro-moderno.',
    trims: [
      {
        name: 'Sport',
        auction: [13500, 15500],
        features: ['Motor 3.0TT V6 400hp', 'Pantalla 9"', 'Rines 18"'],
      },
      {
        name: 'Performance',
        auction: [15500, 19000],
        features: ['Rines 19"', 'LSD mecánico', 'Bose'],
      },
    ],
  },
  {
    id: 'gr86',
    make: 'Toyota',
    model: 'GR86',
    yearRange: '2022-2024',
    auction: [8000, 12500],
    type: 'Deportivo',
    engine: '2.4L 4cyl',
    description: 'Coupé deportivo ligero. Hermano del BRZ con alma Toyota.',
    trims: [
      {
        name: 'Base',
        auction: [8000, 10000],
        features: ['Motor 2.4L 228hp', 'Pantalla 8"', 'Rines 17"'],
      },
      {
        name: 'Premium',
        auction: [10000, 12500],
        features: ['Cuero/Alcantara', 'Rines 18"', 'Blind Spot'],
      },
    ],
  },
  {
    id: 'supra-5',
    make: 'Toyota',
    model: 'GR Supra',
    yearRange: '2020-2024',
    auction: [13500, 22000],
    type: 'Deportivo',
    engine: '3.0L 6cyl Turbo',
    description:
      'Leyenda japonesa renacida. Motor turbo BMW y chasis excepcional.',
    trims: [
      {
        name: '2.0',
        auction: [13500, 16000],
        features: ['Motor 2.0T 255hp', 'Pantalla 8.8"', 'Rines 18"'],
      },
      {
        name: '3.0',
        auction: [17000, 19500],
        features: ['Motor 3.0T 382hp', 'Diferencial activo', 'Rines 19"'],
      },
      {
        name: '3.0 Premium',
        auction: [19000, 22000],
        features: ['Cuero', 'JBL Audio', 'Head-up Display'],
      },
    ],
  },
]

export const carData: CarModel[] = buildCarData(rawCarData)

export function estimatePriceForYear(
  car: CarModel,
  year: number,
): { min: number; max: number } {
  const years = getYearsFromRange(car.yearRange)
  if (years.length <= 1) return car.priceRange
  const oldestYear = years[0]
  const totalSpan = years[years.length - 1] - oldestYear
  if (totalSpan === 0) return car.priceRange

  const yearsFromBase = year - oldestYear
  const rates = getInterYearRates(car.make)
  const calibration = getCalibration(car.make)

  const yearAuctionMin = applyYearlyRates(
    car.auctionRange.min,
    rates.minRate,
    yearsFromBase,
    totalSpan,
  )
  const yearAuctionMax = applyYearlyRates(
    car.auctionRange.max,
    rates.maxRate,
    yearsFromBase,
    totalSpan,
  )

  const { avgMin, avgMax } = sampleCosts(
    yearAuctionMin,
    Math.max(yearAuctionMax, yearAuctionMin + 200),
    calibration,
  )

  return { min: avgMin, max: avgMax }
}

/**
 * Estimate a specific trim's price for a given year using
 * per-make inter-year depreciation rates, recency premium,
 * and sampled average costs.
 */
export function estimateTrimPriceForYear(
  car: CarModel,
  trim: TrimLevel,
  year: number,
): { min: number; max: number } {
  const years = getYearsFromRange(car.yearRange)
  const calibration = getCalibration(car.make)

  if (years.length <= 1) {
    const { avgMin, avgMax } = sampleCosts(
      trim.auctionEstimate[0],
      trim.auctionEstimate[1],
      calibration,
    )
    return { min: avgMin, max: avgMax }
  }

  const oldestYear = years[0]
  const totalSpan = years[years.length - 1] - oldestYear
  if (totalSpan === 0) {
    const { avgMin, avgMax } = sampleCosts(
      trim.auctionEstimate[0],
      trim.auctionEstimate[1],
      calibration,
    )
    return { min: avgMin, max: avgMax }
  }

  const yearsFromBase = year - oldestYear
  const rates = getInterYearRates(car.make)

  const adjustedMin = applyYearlyRates(
    trim.auctionEstimate[0],
    rates.minRate,
    yearsFromBase,
    totalSpan,
  )
  const adjustedMax = applyYearlyRates(
    trim.auctionEstimate[1],
    rates.maxRate,
    yearsFromBase,
    totalSpan,
  )

  const { avgMin, avgMax } = sampleCosts(
    adjustedMin,
    Math.max(adjustedMax, adjustedMin + 200),
    calibration,
  )

  return { min: avgMin, max: avgMax }
}
